import { useState } from "react";

const defaultParticipant = {
  nume: "",
  telefon: "",
  email: "",
  varsta: "",
  salariu: "",
  sex: "masculin",
  rateExistente: "",
  carduriCredit: "",
  situatieImobiliara: "primul",
  anConstructie: "",
  clasaEnergetica: "A",
  ircc: "5.68",
  cursEuro: "5.10",
};

// ─── UI primitives ───────────────────────────────────────────────────────────

function InputField({ label, value, onChange, type = "text", placeholder, suffix, required, missing, fieldName, isEmpty }) {
  const fmtErr = fieldName ? getFieldError(fieldName, value) : null;
  const hasErr = missing || !!fmtErr;
  // isEmpty = camp obligatoriu gol dar fara eroare de submit inca
  const isEmptyHighlight = !hasErr && required && isEmpty;

  let borderColor = "#e2e8f0";
  let bgColor = "#fff";
  let labelColor = "#64748b";

  if (hasErr) {
    borderColor = "#fca5a5";
    bgColor = "#fff5f5";
    labelColor = "#ef4444";
  } else if (isEmptyHighlight) {
    borderColor = "#fbbf24";
    bgColor = "#fffbeb";
    labelColor = "#92400e";
  } else if (value && String(value).trim()) {
    borderColor = "#86efac";
    bgColor = "#f0fdf4";
    labelColor = "#065f46";
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 600,
        color: labelColor,
        marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em"
      }}>
        {label}{required && <span style={{ color: hasErr ? "#ef4444" : isEmptyHighlight ? "#f59e0b" : "#94a3b8", marginLeft: 2 }}>*</span>}
      </label>
      <div style={{ position: "relative" }}>
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%", padding: "8px 10px",
            border: `1.5px solid ${borderColor}`,
            borderRadius: 7, fontSize: 13, background: bgColor,
            color: "#1e293b", outline: "none", boxSizing: "border-box",
            paddingRight: suffix ? 38 : 10,
            transition: "border-color 0.2s, background 0.2s",
          }}
        />
        {suffix && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12 }}>{suffix}</span>}
      </div>
      {fmtErr && !missing && <div style={{ fontSize: 10, color: "#ef4444", marginTop: 2 }}>{fmtErr}</div>}
      {isEmptyHighlight && <div style={{ fontSize: 10, color: "#b45309", marginTop: 2 }}>⚠ Câmp obligatoriu</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, required, missing }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 600,
        color: missing ? "#ef4444" : "#64748b",
        marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em"
      }}>
        {label}{required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: "100%", padding: "8px 10px",
          border: `1.5px solid ${missing ? "#fca5a5" : "#e2e8f0"}`,
          borderRadius: 7, fontSize: 13,
          background: missing ? "#fff5f5" : "#fff",
          color: "#1e293b", outline: "none"
        }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ─── Validation ──────────────────────────────────────────────────────────────

const REQUIRED_FIELDS_FULL = ["nume", "telefon", "email", "varsta", "salariu", "anConstructie"];
const REQUIRED_FIELDS_CODEBITOR = ["varsta", "salariu"];

const isValidPhone = (v) => /^07[0-9]{8}$/.test(String(v).trim().replace(/\s/g, ""));
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());
const isValidNume = (v) => { const w = String(v).trim().split(/\s+/).filter(x => x.length >= 2); return w.length >= 2; };
const isValidVarsta = (v) => { const n = parseInt(v); return !isNaN(n) && n >= 18; };
const isValidSalariu = (v) => { const n = parseFloat(v); return !isNaN(n) && n >= 2574; };

function getMissingFields(data, fields = REQUIRED_FIELDS_FULL) {
  const empty = fields.filter(f => !data[f] || String(data[f]).trim() === "");
  const invalid = [];
  if (data.telefon && data.telefon.trim() && !isValidPhone(data.telefon)) invalid.push("telefon");
  if (data.email && data.email.trim() && !isValidEmail(data.email)) invalid.push("email");
  if (data.nume && data.nume.trim() && !isValidNume(data.nume)) invalid.push("nume");
  if (data.varsta && data.varsta.toString().trim() && !isValidVarsta(data.varsta)) invalid.push("varsta");
  if (data.salariu && data.salariu.toString().trim() && !isValidSalariu(data.salariu)) invalid.push("salariu");
  return [...new Set([...empty, ...invalid])];
}

function isComplete(data, fields = REQUIRED_FIELDS_FULL) {
  return getMissingFields(data, fields).length === 0;
}

// Returneaza true daca campul e obligatoriu si gol (pentru highlight subtil permanent)
function isEmptyRequired(fieldName, value, requiredFields) {
  if (!requiredFields.includes(fieldName)) return false;
  return !value || String(value).trim() === "";
}

const isValidYear = (v) => /^[0-9]{4}$/.test(String(v).trim()) && parseInt(v) >= 1800 && parseInt(v) <= new Date().getFullYear();

function getFieldError(field, value) {
  if (field === "telefon" && value && value.trim() && !isValidPhone(value))
    return "Telefonul trebuie să înceapă cu 07 și să aibă 10 cifre (ex: 0722XXXXXX)";
  if (field === "email" && value && value.trim() && !isValidEmail(value))
    return "Adresă de email invalidă";
  if (field === "anConstructie" && value && value.trim() && !isValidYear(value))
    return "Introduceți un an valid (4 cifre)";
  if (field === "nume" && value && value.trim() && !isValidNume(value))
    return "Introduceți numele și prenumele (minim 2 cuvinte)";
  if (field === "varsta" && value && value.toString().trim() && !isValidVarsta(value))
    return "Vârsta minimă este 18 ani";
  if (field === "salariu" && value && value.toString().trim() && !isValidSalariu(value))
    return "Salariul minim acceptat este 2.574 RON";
  return null;
}

// ─── Single participant fields block (reusable) ───────────────────────────────

function ParticipantFields({ data, onChange, showMissing, color, showDatoriiColor, hidePropertyFields = false, hideContactFields = false, requiredFields = REQUIRED_FIELDS_FULL }) {
  const [showDatorii, setShowDatorii] = useState(false);
  const set = f => v => onChange({ ...data, [f]: v });
  const missing = showMissing ? getMissingFields(data, requiredFields) : [];
  const m = f => missing.includes(f);
  // isEmpty: campul e obligatoriu si gol — evidentiaza cu galben inainte de submit
  const empty = f => requiredFields.includes(f) && (!data[f] || String(data[f]).trim() === "");

  return (
    <>
      {!hideContactFields && <>
        <InputField label="Nume Prenume" value={data.nume} onChange={set("nume")} placeholder="ex: Popescu Ion" required missing={m("nume")} isEmpty={empty("nume")} fieldName="nume" />
        <InputField label="Telefon" value={data.telefon} onChange={set("telefon")} placeholder="07xx xxx xxx" required missing={m("telefon")} fieldName="telefon" isEmpty={empty("telefon")} />
        <InputField label="Email" value={data.email} onChange={set("email")} type="email" placeholder="adresa@email.ro" required missing={m("email")} fieldName="email" isEmpty={empty("email")} />
      </>}
      <InputField label="Vârstă (ani)" value={data.varsta} onChange={set("varsta")} type="number" placeholder="minim 18" required missing={m("varsta")} isEmpty={empty("varsta")} fieldName="varsta" />
      <InputField label="Salariu net (RON/lună)" value={data.salariu} onChange={set("salariu")} type="number" placeholder="minim 2.574 RON" suffix="RON" required missing={m("salariu")} isEmpty={empty("salariu")} fieldName="salariu" />
      <SelectField label="Sex" value={data.sex} onChange={set("sex")}
        options={[{ value: "masculin", label: "Masculin" }, { value: "feminin", label: "Feminin" }]} />

      {/* Datorii existente */}
      <div style={{ marginBottom: 10 }}>
        <button onClick={() => setShowDatorii(!showDatorii)} style={{
          width: "100%", padding: "8px 10px",
          background: showDatorii ? (showDatoriiColor || "#f0f9ff") : "#f8fafc",
          border: `1.5px solid ${showDatorii ? (color || "#bae6fd") : "#e2e8f0"}`,
          borderRadius: showDatorii ? "7px 7px 0 0" : 7,
          fontSize: 12, fontWeight: 600, color: "#1e293b", cursor: "pointer",
          textAlign: "left", display: "flex", justifyContent: "space-between",
        }}>
          <span>📋 Datorii existente</span>
          <span>{showDatorii ? "▲" : "▼"}</span>
        </button>
        {showDatorii && (
          <div style={{
            background: showDatoriiColor || "#f0f9ff",
            borderRadius: "0 0 7px 7px",
            border: `1.5px solid ${color || "#bae6fd"}`,
            borderTop: "none", padding: "10px 10px 6px"
          }}>
            <InputField label="Rate existente (RON/lună)" value={data.rateExistente} onChange={set("rateExistente")} type="number" placeholder="0" suffix="RON" />
            <InputField label="Carduri credit/overdraft – limită aprobată (RON)" value={data.carduriCredit} onChange={set("carduriCredit")} type="number" placeholder="0" suffix="RON" />
          </div>
        )}
      </div>

      <SelectField label="Situație imobiliară" value={data.situatieImobiliara} onChange={set("situatieImobiliara")}
        options={[{ value: "primul", label: "Primul imobil" }, { value: "detin", label: "Dețin o proprietate" }]} />
      {!hidePropertyFields && (
        <>
          <InputField label="Anul construcției imobilului achiziționat" value={data.anConstructie} onChange={set("anConstructie")} type="number" placeholder="ex: 2010" required missing={m("anConstructie")} fieldName="anConstructie" isEmpty={empty("anConstructie")} />
          <SelectField label="Clasa energetică" value={data.clasaEnergetica} onChange={set("clasaEnergetica")}
            options={[{ value: "A", label: "Clasa A" }, { value: "B", label: "Clasa B" }, { value: "alta", label: "Altă clasă" }]} />
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Valoarea IRCC (%)</label>
              <div style={{ position: "relative" }}>
                <input type="number" value={data.ircc} onChange={e => onChange({ ...data, ircc: e.target.value })}
                  placeholder="5.68"
                  style={{ width: "100%", padding: "8px 28px 8px 10px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box" }} />
                <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12 }}>%</span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Curs EUR</label>
              <div style={{ position: "relative" }}>
                <input type="number" value={data.cursEuro || "5.10"} onChange={e => onChange({ ...data, cursEuro: e.target.value })}
                  placeholder="5.10"
                  style={{ width: "100%", padding: "8px 38px 8px 10px", border: "1.5px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box" }} />
                <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12 }}>RON</span>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ─── Main form card ───────────────────────────────────────────────────────────

function MainForm({ p1, setP1, p2, setP2, codebitorActive, setCodebitorActive, showMissing }) {
  return (
    <div style={{
      background: "#fff",
      border: "2px solid #1a6de0",
      borderRadius: 14,
      padding: "18px 16px",
    }}>
      {/* Title */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14, gap: 8 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#1a6de0" }} />
        <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Solicitant Principal</span>
      </div>

      <ParticipantFields data={p1} onChange={setP1} showMissing={showMissing} color="#bae6fd" showDatoriiColor="#f0f9ff" />

      {/* Codebitor selector */}
      <div style={{ marginBottom: 10, marginTop: 4 }}>
        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Codebitor
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setCodebitorActive(false)}
            style={{
              flex: 1, padding: "9px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: `2px solid ${!codebitorActive ? "#1a6de0" : "#e2e8f0"}`,
              background: !codebitorActive ? "#eff6ff" : "#f8fafc",
              color: !codebitorActive ? "#1a6de0" : "#94a3b8",
              cursor: "pointer", transition: "all 0.2s",
            }}
          >
            Fără codebitor
          </button>
          <button
            onClick={() => setCodebitorActive(true)}
            style={{
              flex: 1, padding: "9px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: `2px solid ${codebitorActive ? "#f97316" : "#e2e8f0"}`,
              background: codebitorActive ? "#fff7ed" : "#f8fafc",
              color: codebitorActive ? "#ea580c" : "#94a3b8",
              cursor: "pointer", transition: "all 0.2s",
            }}
          >
            Cu codebitor
          </button>
        </div>
      </div>

      {/* Codebitor sub-form */}
      {codebitorActive && (
        <div style={{
          marginTop: 14,
          border: "2px dashed #f97316",
          borderRadius: 12,
          padding: "14px 14px 10px",
          background: "#fffbf5",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f97316" }} />
            <span style={{ fontWeight: 700, fontSize: 13, color: "#c2410c" }}>Codebitor</span>
          </div>
          <ParticipantFields data={p2} onChange={setP2} showMissing={showMissing} color="#fed7aa" showDatoriiColor="#fff7ed" hidePropertyFields={true} hideContactFields={true} requiredFields={REQUIRED_FIELDS_CODEBITOR} />
        </div>
      )}
    </div>
  );
}

// ─── Calculation ─────────────────────────────────────────────────────────────

function calcMonthlyPayment(principal, annualRate, months) {
  if (!principal || !months || annualRate == null) return 0;
  const r = annualRate / 100 / 12;
  if (r === 0) return principal / months;
  return principal * r * Math.pow(1 + r, months) / (Math.pow(1 + r, months) - 1);
}

// Calcul total de plată conform metodologiei bancare verificate pe scadentare reale:
// - Rata fixă calculată pe DURATA REALĂ a clientului (totalYears)
// - Sold după perioada fixă = amortizare reală cu rata fixă
// - Rata variabilă calculată pe soldul rămas și lunile rămase din durata clientului
// - Asigurare de viață descrescătoare: calculată lunar pe sold curent (lifeInsRate = % lunar)
// - Comision anual BT: 150 RON/an
function calcTotalPlata(sumCredit, fixedRate, fixedYears, variableRate, totalYears, lifeInsRate, comisionAnual = 0) {
  const nTotal = totalYears * 12;
  const nFix = Math.min(fixedYears * 12, nTotal);
  const nVar = nTotal - nFix;
  const rFix = fixedRate / 100 / 12;
  const rVar = variableRate / 100 / 12;

  // Rata fixă calculată pe durata reală a clientului
  const rataFix = calcMonthlyPayment(sumCredit, fixedRate, nTotal);

  // Sold după perioada fixă (amortizare reală)
  let sold = sumCredit;
  for (let i = 0; i < nFix; i++) {
    const dob = sold * rFix;
    sold = Math.max(sold - (rataFix - dob), 0);
  }
  const soldDupaFix = sold;

  // Rata variabilă pe soldul rămas și lunile rămase
  const rataVar = nVar > 0 ? calcMonthlyPayment(soldDupaFix, variableRate, nVar) : 0;

  // Total rate
  let totalRate = rataFix * nFix + rataVar * nVar;

  // Asigurare descrescătoare (lifeInsRate = % lunar, ex: 0.0259 = 0.0259%/lună)
  let totalAsig = 0;
  sold = sumCredit;
  if (lifeInsRate > 0) {
    for (let i = 0; i < nFix; i++) {
      totalAsig += sold * lifeInsRate / 100;
      const dob = sold * rFix;
      sold = Math.max(sold - (rataFix - dob), 0);
    }
    for (let i = 0; i < nVar; i++) {
      totalAsig += sold * lifeInsRate / 100;
      const dob = sold * rVar;
      sold = Math.max(sold - (rataVar - dob), 0);
    }
  }

  // Comision anual BT
  const totalComision = comisionAnual * totalYears;

  const totalDePlata = totalRate + totalAsig + totalComision;
  const costTotal = totalDePlata - sumCredit;
  return { totalDePlata, costTotal };
}

function formatRON(val) {
  if (!val && val !== 0) return "-";
  return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(val)) + " RON";
}

// Formatare cu suport valuta — folosita in componente care au acces la currency/cursEuro
function fmtVal(val, currency, cursEuro) {
  if (!val && val !== 0) return "-";
  const curs = parseFloat(cursEuro) || 5.10;
  if (currency === "EUR") {
    const eur = Math.round(val / curs);
    return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(eur) + " EUR";
  }
  return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(val)) + " RON";
}

function computeResults(p1, p2, codebitorActive) {
  const ircc = parseFloat(p1.ircc) || 5.68;
  const sal1 = parseFloat(p1.salariu) || 0;
  const sal2 = codebitorActive ? (parseFloat(p2.salariu) || 0) : 0;
  const totalSalariu = sal1 + sal2;

  // Grad si avans:
  // - Ambii la primul imobil: grad 45%, avans 15%
  // - Ambii detin proprietate: grad 40%, avans 25%
  // - Selectii diferite (unul primul, altul detin): varianta nefavorabila => grad 40%, avans 25%
  const p1PrimulImobil = p1.situatieImobiliara === "primul";
  const p2PrimulImobil = codebitorActive && p2.situatieImobiliara === "primul";
  const ambiiiPrimulImobil = p1PrimulImobil && (!codebitorActive || p2PrimulImobil);
  const primuImobil = ambiiiPrimulImobil; // folosit pentru mesaje/conditii
  const gradIndatorare = ambiiiPrimulImobil ? 0.45 : 0.40;
  const avans = ambiiiPrimulImobil ? 0.15 : 0.25;

  const rate1 = parseFloat(p1.rateExistente) || 0;
  const carduri1 = parseFloat(p1.carduriCredit) || 0;
  const rate2 = codebitorActive ? (parseFloat(p2.rateExistente) || 0) : 0;
  const carduri2 = codebitorActive ? (parseFloat(p2.carduriCredit) || 0) : 0;

  const anConstr = parseInt(p1.anConstructie) || 0;
  const clasa = p1.clasaEnergetica;
  const isBefore2000 = anConstr > 0 && anConstr < 2000;
  const isAB = clasa === "A" || clasa === "B";
  const isA = clasa === "A";
  const anyOver10k = sal1 >= 10000 || (codebitorActive && sal2 >= 10000);

  const age1 = parseInt(p1.varsta) || 30;
  const age2 = codebitorActive ? (parseInt(p2.varsta) || 30) : 0;

  function brdAdjustedIncome(participant, active) {
    if (!active) return 0;
    const sal = parseFloat(participant.salariu) || 0;
    const age = parseInt(participant.varsta) || 30;
    const retAge = participant.sex === "masculin" ? 65 : 63;
    const yearsToRet = retAge - age;
    if (yearsToRet >= 17) return sal;
    const adjusted = 0.429 * sal * Math.pow(1 + 0.052, yearsToRet);
    return Math.min(adjusted, sal);
  }

  const banks = [];

  // ---- BCR ----
  {
    const maxBCR1 = (p1.sex === "masculin" ? 65 : 63) - age1;
    const maxBCR2 = codebitorActive ? ((p2.sex === "masculin" ? 65 : 63) - age2) : 30;
    const maxYears = Math.min(30, maxBCR1, maxBCR2);
    if (maxYears > 0) {
      const calcRate = (!isBefore2000 && isAB) ? (3.3 + ircc) : (3.5 + ircc);
      const lifeIns = 0.026 / 100;
      const totalDebt = (rate1 + 0.05 * carduri1) + (codebitorActive ? (rate2 + 0.05 * carduri2) : 0);
      const rataDisponibila = gradIndatorare * totalSalariu - totalDebt;
      if (rataDisponibila > 0) {
        const r = calcRate / 100 / 12; const n = maxYears * 12;
        const pmtF = r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
        const sumCredit = rataDisponibila / (pmtF + lifeIns);
        const asigurareViata = sumCredit * lifeIns;
        const valoareImobil = sumCredit / (1 - avans);
        // Variante dobânda: [fixedRate, fixedYears, variableSpread]
        const isGoodImobil = !isBefore2000 && isAB;
        const bcrVariants = isGoodImobil
          ? anyOver10k
            ? [[4.79, 3, 2.10], [5.59, 5, 2.10]]
            : [[4.99, 3, 2.10], [5.79, 5, 2.10]]
          : anyOver10k
            ? [[4.99, 3, 2.30], [5.79, 5, 2.30]]
            : [[5.19, 3, 2.30], [5.99, 5, 2.30]];
        for (const [fr, fy, vs] of bcrVariants) {
          banks.push({
            name: "BCR",
            dobanda: `${fr}% fix ${fy} ani, apoi IRCC+${vs}%`,
            fixedRate: fr, variableRate: ircc + vs,
            sumCredit, valoareImobil, avansVal: valoareImobil * avans, avans: avans * 100,
            rataLunara: calcMonthlyPayment(sumCredit, fr, n), maxDurata: maxYears,
            asigurareViata, lifeInsRate: 0.026,
            conditii: [`Durată max. ${maxYears} ani`, !primuImobil ? "Avans 25%" : ""].filter(Boolean),
            mentiuneImobil: primuImobil && codebitorActive && p2.situatieImobiliara === "primul" ? "Rol inversat posibil – grad 45%" : "",
          });
        }
      }
    }
  }

  // ---- UNICREDIT ----
  // calcRate diferit per period: 2yr=2.3, 3yr=2.5, 5yr=2.6 (+IRCC)
  {
    const maxUni1 = 65 - age1;
    const maxUni2 = codebitorActive ? 65 - age2 : 35;
    const maxYears = Math.min(35, maxUni1, maxUni2);
    if (maxYears > 0) {
      const lifeIns = 0.0259 / 100;
      const debtTotal = (rate1 + 0.03 * carduri1) + (codebitorActive ? rate2 + 0.03 * carduri2 : 0);
      const n = maxYears * 12;
      // Variante: [fixedRate, fixedYears, variableSpread, calcRateSpread]
      // 2yr fix: calcRate 2.3+ircc; 3yr fix: 2.5+ircc; 5yr fix: 2.6+ircc
      const uniVariants = isA && anyOver10k
        ? [[4.59, 2, 2.20, 2.3], [4.99, 3, 2.20, 2.5], [5.90, 5, 2.40, 2.6]]
        : [[4.69, 2, 2.25, 2.3], [5.29, 3, 2.30, 2.5], [5.90, 5, 2.40, 2.6]];
      for (const [fr, fy, vs, crs] of uniVariants) {
        const calcRate = crs + ircc;
        const r = calcRate / 100 / 12;
        const pmtF = r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
        const rataDisponibila = gradIndatorare * totalSalariu - debtTotal;
        if (rataDisponibila > 0) {
          const sumCredit = rataDisponibila / (pmtF + lifeIns);
          const asigurareViata = sumCredit * lifeIns;
          const valoareImobil = sumCredit / (1 - avans);
          banks.push({
            name: "UniCredit Bank",
            dobanda: `${fr}% fix ${fy} ani, apoi IRCC+${vs}%`,
            fixedRate: fr, variableRate: ircc + vs,
            sumCredit, valoareImobil, avansVal: valoareImobil * avans, avans: avans * 100,
            rataLunara: calcMonthlyPayment(sumCredit, fr, n), maxDurata: maxYears,
            asigurareViata, lifeInsRate: 0.0259,
            conditii: [`Durată max. ${maxYears} ani`],
            mentiuneImobil: "",
          });
        }
      }
    }
  }

  // ---- BRD ----
  {
    const maxBRD1 = 76 - age1;
    const maxBRD2 = codebitorActive ? 76 - age2 : 30;
    const maxYears = Math.min(30, maxBRD1, maxBRD2);
    if (maxYears > 0) {
      const calcRate = isA ? (2.1 + ircc) : (2.3 + ircc);
      const lifeIns = 0.033 / 100;
      const adj1 = brdAdjustedIncome(p1, true);
      const adj2 = brdAdjustedIncome(p2, codebitorActive);
      const adjTotal = adj1 + adj2;
      const debtTotal = (rate1 + 0.03 * carduri1) + (codebitorActive ? rate2 + 0.03 * carduri2 : 0);
      const rataDisponibila = gradIndatorare * adjTotal - debtTotal;
      if (rataDisponibila > 0) {
        const r = calcRate / 100 / 12; const n = maxYears * 12;
        const pmtF = r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
        const sumCredit = rataDisponibila / (pmtF + lifeIns);
        const valoareImobil = sumCredit / (1 - avans);
        // Label avans BRD: >25% daca detine proprietate (avans=25%), >15% daca primul imobil
        const avansLabelBRD = avans * 100 >= 25 ? "avans >25%" : "avans >15%";
        const variants = isA
          ? [{ fr: 4.99, vs: 2.10, cond: `Clasă A + ${avansLabelBRD}` }]
          : [{ fr: 5.30, vs: 2.29, cond: `Clasă ≠A + ${avansLabelBRD}` }];
        if (avans * 100 < 20) isA
          ? variants.push({ fr: 5.19, vs: 2.30, cond: "Clasă A + avans <20%" })
          : variants.push({ fr: 5.50, vs: 2.49, cond: "Clasă ≠A + avans <20%" });
        const asigurareViataBRD = sumCredit * lifeIns;
        for (const v of variants) {
          banks.push({
            name: "BRD",
            dobanda: `${v.fr}% fix 3 ani, apoi IRCC+${v.vs}%`,
            fixedRate: v.fr, variableRate: ircc + v.vs,
            sumCredit, valoareImobil, avansVal: valoareImobil * avans, avans: avans * 100,
            rataLunara: calcMonthlyPayment(sumCredit, v.fr, n), maxDurata: maxYears,
            asigurareViata: asigurareViataBRD, lifeInsRate: 0.033,
            conditii: [v.cond, `Durată max. ${maxYears} ani`,
              (adj1 < sal1 || (codebitorActive && adj2 < sal2)) ? "Venit ponderat (pensionare aproape)" : ""].filter(Boolean),
            mentiuneImobil: "",
          });
        }
      }
    }
  }

  // ---- BANCA TRANSILVANIA ----
  {
    const maxBT1 = 65 - age1;
    const maxBT2 = codebitorActive ? 65 - age2 : 30;
    const maxYears = Math.min(30, maxBT1, maxBT2);
    if (maxYears > 0) {
      const calcRate = 2.8 + ircc;
      const debtTotal = (rate1 + 0.03 * carduri1) + (codebitorActive ? rate2 + 0.03 * carduri2 : 0);
      const rataDisponibila = gradIndatorare * totalSalariu - debtTotal;
      if (rataDisponibila > 0) {
        const r = calcRate / 100 / 12; const n = maxYears * 12;
        const pmtF = r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
        const sumCredit = rataDisponibila / pmtF;
        const valoareImobil = sumCredit / (1 - avans);
        const variants = isA
          ? [{ fr: 4.99, vs: 1.99, fy: 2 }, { fr: 5.15, vs: 2.10, fy: 3 }]
          : [{ fr: 5.09, vs: 1.99, fy: 2 }, { fr: 5.20, vs: 2.10, fy: 3 }];
        for (const v of variants) {
          banks.push({
            name: "Banca Transilvania",
            dobanda: `${v.fr}% fix ${v.fy} ani, apoi IRCC+${v.vs}%`,
            fixedRate: v.fr, variableRate: ircc + v.vs,
            sumCredit, valoareImobil, avansVal: valoareImobil * avans, avans: avans * 100,
            rataLunara: calcMonthlyPayment(sumCredit, v.fr, n), maxDurata: maxYears,
            asigurareViata: 0, lifeInsRate: 0,
            conditii: [`Durată max. ${maxYears} ani`],
            mentiuneImobil: "",
          });
        }
      }
    }
  }

  // ---- EXIM BANK ----
  {
    const maxE1 = 65 - age1;
    const maxE2 = codebitorActive ? 65 - age2 : 30;
    const maxYears = Math.min(30, maxE1, maxE2);
    if (maxYears > 0) {
      const calcRate = isA ? (2.29 + ircc) : (2.5 + ircc);
      const debtTotal = (rate1 + 0.03 * carduri1) + (codebitorActive ? rate2 + 0.03 * carduri2 : 0);
      const rataDisponibila = gradIndatorare * totalSalariu - debtTotal;
      if (rataDisponibila > 0) {
        const r = calcRate / 100 / 12; const n = maxYears * 12;
        const pmtF = r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
        const sumCredit = rataDisponibila / pmtF;
        const valoareImobil = sumCredit / (1 - avans);
        const fr = isA ? 4.99 : 5.20; const vs = isA ? 2.29 : 2.50;
        banks.push({
          name: "Exim Bank",
          dobanda: `${fr}% fix 3 ani, apoi IRCC+${vs}%`,
          fixedRate: fr, variableRate: ircc + vs,
          sumCredit, valoareImobil, avansVal: valoareImobil * avans, avans: avans * 100,
          rataLunara: calcMonthlyPayment(sumCredit, fr, n), maxDurata: maxYears,
          asigurareViata: 0, lifeInsRate: 0,
          conditii: [`Durată max. ${maxYears} ani`],
          mentiuneImobil: "",
        });
      }
    }
  }

  // ---- ING BANK ----
  // Afiseaza TOATE variantele de avans (>=40%, 20-40%, <20%) x 2 perioade fixe (3yr, 5yr)
  // Suma maxima se calculeaza consistent per categorie avans+suma
  {
    const maxI1 = (p1.sex === "masculin" ? 65 : 63) - age1;
    const maxI2 = codebitorActive ? ((p2.sex === "masculin" ? 65 : 63) - age2) : 30;
    const maxYears = Math.min(30, maxI1, maxI2);
    if (maxYears > 0) {
      const lifeIns = 0.029 / 100;
      const debtTotal = (rate1 + 0.03 * carduri1) + (codebitorActive ? rate2 + 0.03 * carduri2 : 0);
      const rataDisponibila = gradIndatorare * totalSalariu - debtTotal;
      if (rataDisponibila > 0) {
        const n = maxYears * 12;
        const cls = isA ? "A" : "alta";

        // Tabel complet ING: [calcRateSpread, fr3, vs3, fr5, vs5]
        const ingT = {
          A: {
            mare:  { gte40:[1.89,4.79,1.89,5.29,1.89], "2040":[2.09,4.99,2.09,5.49,2.09], lt20:[2.29,5.19,2.29,5.69,2.29] },
            medie: { gte40:[2.09,4.99,2.09,5.49,2.09], "2040":[2.29,5.19,2.29,5.69,2.29], lt20:[2.49,5.39,2.49,5.89,2.49] },
            mica:  { gte40:[2.29,5.19,2.29,5.69,2.49], "2040":[2.49,5.39,2.49,5.89,2.69], lt20:[2.69,5.59,2.69,6.09,2.89] },
          },
          alta: {
            mare:  { gte40:[2.09,4.99,2.09,5.49,2.09], "2040":[2.29,5.19,2.29,5.69,2.29], lt20:[2.49,5.39,2.49,5.89,2.49] },
            medie: { gte40:[2.29,5.19,2.29,5.69,2.29], "2040":[2.49,5.39,2.49,5.89,2.49], lt20:[2.69,5.59,2.69,6.09,2.69] },
            mica:  { gte40:[2.49,5.39,2.49,5.89,2.49], "2040":[2.69,5.59,2.69,6.09,2.69], lt20:[2.89,5.79,2.89,6.29,2.89] },
          },
        };

        // Algoritm consistent per categorie avans: pentru fiecare avans key, 
        // gaseste suma maxima consistenta (suma calculata se incadreaza in categoria folosita)
        const findIngSum = (aKey) => {
          for (const cat of ["mare", "medie", "mica"]) {
            const row = ingT[cls][cat][aKey];
            const cr = (row[0] + ircc) / 100 / 12;
            const pmtF = cr * Math.pow(1 + cr, n) / (Math.pow(1 + cr, n) - 1);
            const scTry = rataDisponibila / (pmtF + lifeIns);
            const realCat = scTry > 400000 ? "mare" : scTry >= 200000 ? "medie" : "mica";
            if (realCat === cat) return { sc: scTry, row };
            // Inconsistenta - incearca categoria urmatoare (mai restrictiva)
          }
          // Fallback: mica (intotdeauna consistent pentru sume mici)
          const row = ingT[cls]["mica"][aKey];
          const cr = (row[0] + ircc) / 100 / 12;
          const pmtF = cr * Math.pow(1 + cr, n) / (Math.pow(1 + cr, n) - 1);
          return { sc: rataDisponibila / (pmtF + lifeIns), row };
        };

        // Avansul minim al clientului (din situatia imobiliara)
        const avansMinClient = avans * 100; // 15% sau 25%

        // Variante avans ING in functie de avansul minim al clientului:
        // - Primul imobil (avans 15%): toate 3 variante (<20% afisat ca "15-20%", 20-40% ramas, >=40%)
        // - Al doilea imobil (avans 25%): doar 2 variante (20-40% afisat ca "25-40%", >=40%)
        //   varianta <20% se elimina deoarece avansul standard e 25%
        const avansVariants = [];
        // Varianta avans >=40% - mereu disponibila
        avansVariants.push({ aKey: "gte40", label: "avans ≥40%", avansING: 40, over40: true, bet2040: false });
        // Varianta avans 20-40%:
        // - primul imobil: avans minim 20% (poate pune intre 20-40%)
        // - al doilea imobil: avans minim 25% => afisat ca "25-40%"
        const avans2040Min = avansMinClient >= 20 ? avansMinClient : 20;
        const label2040 = avansMinClient >= 25 ? "avans 25-40%" : "avans 20-40%";
        avansVariants.push({ aKey: "2040", label: label2040, avansING: avans2040Min, over40: false, bet2040: true });
        // Varianta avans <20%: doar daca clientul are avans minim sub 20% (primul imobil, 15%)
        if (avansMinClient < 20) {
          avansVariants.push({ aKey: "lt20", label: "avans 15-20%", avansING: avansMinClient, over40: false, bet2040: false });
        }

        for (const { aKey, label, avansING, over40, bet2040 } of avansVariants) {
          const avansDecimal = avansING / 100;
          const { sc, row } = findIngSum(aKey);
          const valImob = sc / (1 - avansDecimal);
          const avansVal = valImob * avansDecimal;
          const asigViata = sc * lifeIns;

          for (const [fr, vs, fy] of [[row[1], row[2], 3], [row[3], row[4], 5]]) {
            banks.push({
              name: "ING Bank",
              dobanda: `${fr}% fix ${fy} ani, apoi IRCC+${vs}%`,
              fixedRate: fr, variableRate: ircc + vs,
              sumCredit: sc, valoareImobil: valImob, avansVal, avans: avansING,
              rataLunara: calcMonthlyPayment(sc, fr, n), maxDurata: maxYears,
              asigurareViata: asigViata, lifeInsRate: 0.029,
              conditii: [label, `Durată max. ${maxYears} ani`],
              mentiuneImobil: "",
              ingTable: true, ingIsA: isA, ingAvansOver40: over40,
              ingAvansBetween2040: bet2040, ingFixedYears: fy, ingIrcc: ircc,
            });
          }
        }
      }
    }
  }

  // Calcul total de plată CORECT per bancă
  banks.forEach(b => {
    const fyMatch = b.dobanda.match(/fix (\d+) ani/);
    const fixedYears = fyMatch ? parseInt(fyMatch[1]) : 0;
    const comisionAnual = b.name === "Banca Transilvania" ? 150 : 0;
    const calcResult = calcTotalPlata(
      b.sumCredit, b.fixedRate, fixedYears,
      b.variableRate, b.maxDurata,
      b.lifeInsRate, comisionAnual
    );
    b.totalPlata = calcResult.totalDePlata;
    b.costTotal = calcResult.costTotal;
  });

  banks.sort((a, b) => a.fixedRate - b.fixedRate);
  return { banks, gradIndatorare: gradIndatorare * 100, avans: avans * 100, primuImobil, totalSalariu };
}

// ─── Bank result card ─────────────────────────────────────────────────────────

const BANK_COLORS = {
  "BCR": "#e63946", "UniCredit Bank": "#c62a2a", "BRD": "#16a34a",
  "Banca Transilvania": "#2563eb", "Exim Bank": "#7c3aed", "ING Bank": "#f97316",
};

// Alias-uri anonime pentru afisare publica
// Ordinea corespunde sortarii default (dobanda) — se actualizeaza dinamic in render
const BANK_ALIAS = {
  "BCR": "Ofertă Instituție Financiară A",
  "UniCredit Bank": "Ofertă Instituție Financiară B",
  "BRD": "Ofertă Instituție Financiară C",
  "Banca Transilvania": "Ofertă Instituție Financiară D",
  "Exim Bank": "Ofertă Instituție Financiară E",
  "ING Bank": "Ofertă Instituție Financiară F",
};

const BANK_DETAILS = {
  "BCR": {
    items: [
      { icon: "📋", text: "Comision analiză dosar: 900 RON" },
      { icon: "🏠", text: "Comision evaluare proprietate: 590 RON (apartament) / 790 RON (casă)" },
      { icon: "📝", text: "Cost opinie notarială: aproximativ 250 RON" },
      { icon: "🎁", text: "Bonus: asigurare locuință facultativă GRATUITĂ în primul an prin Omniasig" },
      { icon: "💰", text: "Bonus: 2.000 RON pentru refinanțări externe" },
    ]
  },
  "UniCredit Bank": {
    items: [
      { icon: "📋", text: "Comision analiză dosar: 900 RON" },
      { icon: "🏠", text: "Comision evaluare proprietate: aproximativ 700-900 RON (evaluator extern)" },
      { icon: "🎁", text: "Bonus: 2.000 RON dacă se optează pentru asigurarea facultativă a imobilului prin Allianz" },
    ]
  },
  "BRD": {
    items: [
      { icon: "📋", text: "Comision analiză dosar: 900 RON (0 RON pentru refinanțare de la altă bancă)" },
      { icon: "🏠", text: "Comision evaluare proprietate: 0 RON (evaluator intern)" },
    ]
  },
  "Banca Transilvania": {
    items: [
      { icon: "📋", text: "Comision analiză dosar: 500 RON" },
      { icon: "🏠", text: "Comision evaluare proprietate: 860 RON" },
      { icon: "💳", text: "Comision administrare cont anual: 150 RON" },
      { icon: "🛡️", text: "Asigurarea de viață a solicitantului principal este opțională" },
      { icon: "💰", text: "Bonus: 2.000 RON pentru refinanțări externe" },
    ]
  },
  "Exim Bank": {
    items: [
      { icon: "📋", text: "Comision analiză dosar: 500 RON" },
      { icon: "🏠", text: "Comision evaluare proprietate: aproximativ 700-900 RON (evaluator extern)" },
      { icon: "🛡️", text: "Asigurarea de viață a solicitantului principal este opțională" },
      { icon: "💰", text: "Bonus: 2.500 RON pentru refinanțări externe" },
    ]
  },
  "ING Bank": {
    items: [
      { icon: "📋", text: "Comision analiză dosar: 900 RON (0 RON pentru convenție salarială sau refinanțări externe)" },
      { icon: "🏠", text: "Comision evaluare proprietate: aproximativ 690 RON" },
    ]
  },
};

function ScenarioBox({ label, labelColor, bg, border, rataLunara, asigurare, total, costTotal, durata, color, maxima, currency, cursEuro }) {
  const fmt = (v) => fmtVal(v, currency, cursEuro);
  return (
    <div style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: labelColor, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase" }}>Rată lunară credit</div>
          <div style={{ fontWeight: 700, color: maxima ? color : "#1e293b", fontSize: 13 }}>{fmt(rataLunara)}</div>
        </div>
        {asigurare > 0 && (
          <div>
            <div style={{ fontSize: 10, color: "#0369a1", textTransform: "uppercase" }}>Asigurare/lună</div>
            <div style={{ fontWeight: 700, color: "#0369a1", fontSize: 13 }}>{fmt(asigurare)}</div>
          </div>
        )}
        {asigurare > 0 && (
          <div>
            <div style={{ fontSize: 10, color: "#475569", textTransform: "uppercase" }}>Total/lună (incl. asig.)</div>
            <div style={{ fontWeight: 700, color: "#475569", fontSize: 13 }}>{fmt(rataLunara + asigurare)}</div>
          </div>
        )}
        <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e2e8f0", paddingTop: 6, marginTop: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
            <div>
              <div style={{ fontSize: 10, color: "#7c3aed", textTransform: "uppercase", fontWeight: 700 }}>Total de plată ({durata} ani)</div>
              <div style={{ fontWeight: 700, color: "#7c3aed", fontSize: 13 }}>{fmt(total)}</div>
            </div>
            {costTotal > 0 && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, color: "#9333ea", textTransform: "uppercase", fontWeight: 700 }}>Cost (dob.+asig.)</div>
                <div style={{ fontWeight: 700, color: "#9333ea", fontSize: 13 }}>{fmt(costTotal)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BankCard({ bank, index, showSpecific, currency, cursEuro, showBankName }) {
  const color = BANK_COLORS[bank.name] || "#64748b";
  const incadrat = bank.incadrat;
  const [showDetails, setShowDetails] = useState(false);
  const details = BANK_DETAILS[bank.name];
  const fmt = (v) => fmtVal(v, currency, cursEuro);
  const displayName = showBankName ? bank.name : `Oferta ${index + 1}`;

  return (
    <div style={{
      background: "#fff",
      border: showSpecific
        ? `2px solid ${incadrat ? "#86efac" : "#fca5a5"}`
        : `2px solid ${color}20`,
      borderLeft: `4px solid ${showSpecific ? (incadrat ? "#16a34a" : "#ef4444") : color}`,
      borderRadius: 10, padding: "14px 16px", marginBottom: 10,
      transition: "border-color 0.2s",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ background: showSpecific ? (incadrat ? "#16a34a" : "#ef4444") : color, color: "#fff", borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>#{index + 1}</span>
            <span style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>{displayName}</span>
            {showSpecific && (
              <span style={{
                fontSize: 11, fontWeight: 700, borderRadius: 4, padding: "2px 8px",
                background: incadrat ? "#dcfce7" : "#fee2e2",
                color: incadrat ? "#15803d" : "#dc2626",
              }}>
                {incadrat ? "✅ Eligibil" : "❌ Depășit"}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
            <span style={{ fontWeight: 600, color: "#1e293b" }}>{bank.dobanda}</span>
            <span style={{ color: "#94a3b8", marginLeft: 8 }}>(variabilă: {bank.variableRate.toFixed(2)}%)</span>
          </div>
        </div>
        {!showSpecific && (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Rată lunară</div>
            <div style={{ fontSize: 22, fontWeight: 800, color }}>{fmt(bank.rataLunara)}</div>
            {bank.asigurareViata > 0 && (
              <div style={{ fontSize: 11, color: "#0369a1", marginTop: 2 }}>+ {fmt(bank.asigurareViata)} asig.</div>
            )}
            {bank.asigurareViata > 0 && (
              <div style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>= {fmt(bank.rataLunara + bank.asigurareViata)} total/lună</div>
            )}
          </div>
        )}
      </div>

      {/* Suma maxima row (always shown) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginTop: 12, background: "#f8fafc", borderRadius: 8, padding: "10px 12px" }}>
        <div>
          <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Sumă credit max.</div>
          <div style={{ fontWeight: 700, color: color, fontSize: 13 }}>{fmt(bank.sumCredit)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Valoare imobil max.</div>
          <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>{fmt(bank.valoareImobil)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Avans ({bank.avans.toFixed(0)}%)</div>
          <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>{fmt(bank.avansVal)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: bank.asigurareViata > 0 ? "#0369a1" : "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: bank.asigurareViata > 0 ? 600 : 400 }}>
            Asigurare viață/lună {bank.lifeInsRate > 0 ? `(${bank.lifeInsRate}%)` : ""}
          </div>
          <div style={{ fontWeight: 700, color: bank.asigurareViata > 0 ? "#0369a1" : "#16a34a", fontSize: 13 }}>
            {bank.asigurareViata > 0 ? fmt(bank.asigurareViata) : "Nu se aplică"}
          </div>
        </div>
      </div>

      {/* Dual scenario (specific mode) */}
      {showSpecific ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
          <ScenarioBox
            label={`📋 Sumă solicitată: ${fmt(bank.scSolicitat)} · ${bank.durataAfisata || bank.maxDurata} ani${bank.ingTable && bank.frSolicitat !== bank.fixedRate ? ` · ${bank.frSolicitat}% fix` : ""}`}
            labelColor={incadrat ? "#15803d" : "#dc2626"}
            bg={incadrat ? "#f0fdf4" : "#fef2f2"}
            border={incadrat ? "#86efac" : "#fca5a5"}
            rataLunara={bank.rataSolicitata}
            asigurare={bank.asigSolicitata}
            total={bank.totalSolicitat}
            costTotal={bank.costTotalSolicitat || 0}
            durata={bank.durataAfisata || bank.maxDurata}
            color={color}
            maxima={false}
            currency={currency}
            cursEuro={cursEuro}
          />
          <ScenarioBox
            label={`🏆 Sumă maximă: ${fmt(bank.sumCredit)} · ${bank.maxDurata} ani`}
            labelColor={color}
            bg="#f8fafc"
            border="#e2e8f0"
            rataLunara={bank.rataLunara}
            asigurare={bank.asigurareViata}
            total={bank.totalPlata}
            costTotal={bank.costTotal || 0}
            durata={bank.maxDurata}
            color={color}
            maxima={true}
            currency={currency}
            cursEuro={cursEuro}
          />
        </div>
      ) : (
        <div style={{ marginTop: 8, background: "#f5f3ff", borderRadius: 7, padding: "8px 12px", borderLeft: "3px solid #7c3aed" }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Total de plată ({bank.maxDurata} ani)</div>
              <div style={{ fontWeight: 700, color: "#7c3aed", fontSize: 14 }}>{fmt(bank.totalPlata)}</div>
            </div>
            {bank.costTotal > 0 && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, color: "#9333ea", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Cost total (dobânzi + asig.)</div>
                <div style={{ fontWeight: 700, color: "#9333ea", fontSize: 14 }}>{fmt(bank.costTotal)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {bank.conditii.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {bank.conditii.map((c, i) => (
            <span key={i} style={{ background: `${color}15`, color, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 500 }}>{c}</span>
          ))}
        </div>
      )}
      {bank.mentiuneImobil && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#7c3aed", background: "#f5f3ff", borderRadius: 4, padding: "4px 8px" }}>
          ℹ️ {bank.mentiuneImobil}
        </div>
      )}

      {details && (
        <div style={{ marginTop: 10 }}>
          <button
            onClick={() => setShowDetails(!showDetails)}
            style={{
              width: "100%", padding: "8px 12px", fontSize: 12, fontWeight: 600,
              background: showDetails ? `${color}15` : "#f8fafc",
              border: `1.5px solid ${showDetails ? color : "#e2e8f0"}`,
              borderRadius: 7, color: showDetails ? color : "#64748b",
              cursor: "pointer", textAlign: "left",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              transition: "all 0.2s",
            }}
          >
            <span>ℹ️ Costuri și detalii {displayName}{showBankName ? ` (${bank.name})` : ""}</span>
            <span style={{ fontSize: 10 }}>{showDetails ? "▲ Ascunde" : "▼ Află detalii"}</span>
          </button>
          {showDetails && (
            <div style={{
              background: `${color}08`, border: `1.5px solid ${color}30`,
              borderTop: "none", borderRadius: "0 0 8px 8px", padding: "12px 14px",
            }}>
              {details.items.map((item, i) => (
                <div key={i} style={{
                  display: "flex", gap: 8, alignItems: "flex-start",
                  padding: "5px 0",
                  borderBottom: i < details.items.length - 1 ? "1px solid #e2e8f030" : "none",
                }}>
                  <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                  <span style={{ fontSize: 12, color: "#1e293b", lineHeight: 1.5 }}>{item.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [p1, setP1] = useState({ ...defaultParticipant });
  const [p2, setP2] = useState({ ...defaultParticipant });
  const [codebitorActive, setCodebitorActive] = useState(false);
  const [results, setResults] = useState(null);
  const [calculated, setCalculated] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [sortBy, setSortBy] = useState("dobanda");
  const [mode, setMode] = useState("maxim"); // "maxim" | "specific"
  const [sumaSpecifica, setSumaSpecifica] = useState({ sumCredit: "", avans: "", pretImobil: "", perioadaLuni: "" });
  const [avansRaw, setAvansRaw] = useState(""); // valoarea bruta tastata de utilizator
  const [visibleCount, setVisibleCount] = useState(5); // paginare oferte
  const [specificCalcDone, setSpecificCalcDone] = useState(false);
  const [currency, setCurrency] = useState("RON");
  const [showBankNames, setShowBankNames] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockInput, setLockInput] = useState("");
  const [lockError, setLockError] = useState(false);
  const [tc1, setTc1] = useState(false); // termeni si conditii
  const [tc2, setTc2] = useState(false); // GDPR
  const [tc3, setTc3] = useState(false); // solicit oferta
  const [tc4, setTc4] = useState(false); // 18 ani

  const p1Complete = isComplete(p1);
  const p2Complete = !codebitorActive || isComplete(p2, REQUIRED_FIELDS_CODEBITOR);
  const canCalculate = p1Complete && p2Complete && tc1 && tc2 && tc3 && tc4;

  // Recalculeaza automat daca exista un calcul activ, altfel reseteaza
  const recalcOrReset = (newP1, newP2, newCodActive) => {
    const _p1 = newP1 !== undefined ? newP1 : p1;
    const _p2 = newP2 !== undefined ? newP2 : p2;
    const _cod = newCodActive !== undefined ? newCodActive : codebitorActive;
    const _complete = isComplete(_p1) && (!_cod || isComplete(_p2, REQUIRED_FIELDS_CODEBITOR));
    if (calculated && _complete) {
      // Recalculeaza imediat cu noile date
      const r = computeResults(_p1, _p2, _cod);
      setResults(r);
      setSpecificCalcDone(false);
      setVisibleCount(5);
      // Reseteaza campurile specifice daca avansul minim s-a schimbat
      const p1Prim = _p1.situatieImobiliara === "primul";
      const p2Prim = _cod && _p2.situatieImobiliara === "primul";
      const ambiiiPrim = p1Prim && (!_cod || p2Prim);
      const newAvansMinPct = ambiiiPrim ? 15 : 25;
      setSumaSpecifica(prev => ({ sumCredit: "", avans: "", pretImobil: "", _avansMinPct: newAvansMinPct }));
    } else {
      setResults(null);
      setCalculated(false);
      setSpecificCalcDone(false);
      setSumaSpecifica({ sumCredit: "", avans: "", pretImobil: "", perioadaLuni: "" });
    }
  };

  const handleP1Change = (val) => { setP1(val); recalcOrReset(val, undefined, undefined); };
  const handleP2Change = (val) => { setP2(val); recalcOrReset(undefined, val, undefined); };
  const handleCodebitorChange = (val) => { setCodebitorActive(val); recalcOrReset(undefined, undefined, val); };

  const handleCalculate = () => {
    if (!canCalculate) { setShowMissing(true); return; }
    const r = computeResults(p1, p2, codebitorActive);
    setResults(r);
    setCalculated(true);
    setShowMissing(false);
    setVisibleCount(5);
  };

  // Sincronizare 3 campuri — reguli clare, fara calcule circulare:
  //
  // sumCredit modificat:
  //   pretImobil = sc / (1 - avansMinPct/100)   [avans minim obligatoriu]
  //   avans      = pretImobil - sc
  //
  // avans modificat (liber, >= avansMin din suma credit):
  //   avansMin   = sc * avansMinPct / (100 - avansMinPct)  [% minim din pretImobil]
  //   avFinal    = max(avRaw, avansMin)          [nu poate scadea sub minim]
  //   pretImobil = sc + avFinal                  [suma credit ramane fixa]
  //
  // pretImobil modificat:
  //   avans      = pretImobil * avansMinPct / 100  [avans minim recalculat]
  //   sumCredit  = pretImobil - avans
  //
  const updateSumaSpecifica = (field, val) => {
    const p1Prim = p1.situatieImobiliara === "primul";
    const p2Prim = codebitorActive && p2.situatieImobiliara === "primul";
    const ambiiiPrim = p1Prim && (!codebitorActive || p2Prim);
    const avansMinPct = ambiiiPrim ? 15 : 25;
    let s = { ...sumaSpecifica, [field]: val };

    if (field === "sumCredit") {
      const sc = parseFloat(val) || 0;
      if (sc > 0) {
        // pretImobil astfel incat avansul sa fie exact avansMinPct% din pretImobil
        const pretImobil = Math.round(sc * 100 / (100 - avansMinPct));
        s.pretImobil = pretImobil.toString();
        s.avans = (pretImobil - sc).toString();
      } else {
        s.pretImobil = "";
        s.avans = "";
      }

    } else if (field === "avans") {
      // Avansul e liber — utilizatorul poate pune orice suma >= avans minim
      // Daca avem pretImobil setat: sumCredit = pretImobil - avans (pretImobil ramane fix)
      // Daca avem doar sumCredit: pretImobil = sumCredit + avans (sumCredit ramane fix)
      const avRaw = parseFloat(val) || 0;
      const sc = parseFloat(s.sumCredit) || 0;
      const pt = parseFloat(s.pretImobil) || 0;
      if (pt > 0) {
        // pretImobil setat => avansul modifica sumCredit (pretImobil ramane fix)
        const avMin = Math.round(pt * avansMinPct / 100);
        const avFinal = avRaw >= avMin ? avRaw : avMin;
        s.avans = avFinal.toString();
        const newSc = pt - avFinal;
        s.sumCredit = newSc > 0 ? Math.round(newSc).toString() : "";
      } else if (sc > 0) {
        // Doar sumCredit setat => avansul modifica pretImobil (sumCredit ramane fix)
        const avMin = Math.round(sc * avansMinPct / (100 - avansMinPct));
        const avFinal = avRaw >= avMin ? avRaw : avMin;
        s.avans = avFinal.toString();
        s.pretImobil = (sc + avFinal).toString();
      }

    } else if (field === "pretImobil") {
      // Pretul imobilului se schimba:
      // - Daca avansul existent e >= minim din noul pret => pastreaza avansul, ajusteaza sumCredit
      // - Daca avansul e sub minim sau nu e setat => seteaza avansul la minim, ajusteaza sumCredit
      const pt = parseFloat(val) || 0;
      if (pt > 0) {
        const avMin = Math.round(pt * avansMinPct / 100);
        const avCurent = parseFloat(s.avans) || 0;
        const avFinal = avCurent >= avMin ? avCurent : avMin;
        s.avans = avFinal.toString();
        s.sumCredit = Math.round(pt - avFinal).toString();
      } else {
        s.avans = "";
        s.sumCredit = "";
      }
    }

    setSumaSpecifica(s);
    setSpecificCalcDone(false);
  };

  const computeSpecificResults = () => {
    if (!results) return;
    const sc = parseFloat(sumaSpecifica.sumCredit) || 0;
    if (sc <= 0) return;
    setSpecificCalcDone(true);
  };

    // Sorteaza bancile conform selectiei curente + limita vizibila din ecran
    const getSortedBanks = (banksEnrichedArg, limitCount) => {
      const banks = banksEnrichedArg || results.banks;
      const sorted = [...banks].sort((a, b) => {
        if (sortBy === "dobanda") return a.fixedRate - b.fixedRate;
        if (sortBy === "suma") return b.sumCredit - a.sumCredit;
        if (sortBy === "rata") return a.rataLunara - b.rataLunara;
        if (sortBy === "total") return a.totalPlata - b.totalPlata;
        if (sortBy === "rataSolicitata") return (a.rataSolicitata || 0) - (b.rataSolicitata || 0);
        if (sortBy === "totalSolicitat") return (a.totalSolicitat || 0) - (b.totalSolicitat || 0);
        return 0;
      });
      // limitCount = numarul de oferte vizibile in ecran la momentul apasarii
      return limitCount ? sorted.slice(0, limitCount) : sorted;
    };

    const buildEmailBody = () => {
    if (!results) return "";
    const curs = parseFloat(p1.cursEuro) || 5.10;
    const fmt = (v) => {
      if (!v && v !== 0) return "-";
      if (currency === "EUR") {
        return new Intl.NumberFormat("ro-RO").format(Math.round(v / curs)) + " EUR";
      }
      return new Intl.NumberFormat("ro-RO").format(Math.round(v)) + " RON";
    };
    const sortLabel = {
      dobanda: "Dobândă inițială", suma: "Sumă maximă", rata: "Rată lunară",
      total: "Total de plată", rataSolicitata: "Rată sumă solicitată", totalSolicitat: "Total sumă solicitată"
    }[sortBy] || "Dobândă inițială";

    const date = new Date().toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" });
    let body = `OFERTĂ CREDIT IPOTECAR – creditultau.ro\n`;
    body += `Data: ${date} | Afișare: ${currency} ${currency === "EUR" ? `(1 EUR = ${curs} RON)` : ""}\n`;
    body += `=`.repeat(50) + `\n\n`;
    body += `PARTICIPANȚI:\n`;
    body += `Solicitant principal: ${p1.nume || "-"} | Tel: ${p1.telefon || "-"} | Email: ${p1.email || "-"}\n`;
    body += `Vârstă: ${p1.varsta || "-"} ani | Salariu: ${fmt(parseFloat(p1.salariu))} | ${p1.situatieImobiliara === "primul" ? "Primul imobil" : "Deține proprietate"}\n`;
    if (codebitorActive) {
      body += `\nCodebitor: ${p2.nume || "-"} | Tel: ${p2.telefon || "-"} | Email: ${p2.email || "-"}\n`;
      body += `Vârstă: ${p2.varsta || "-"} ani | Salariu: ${fmt(parseFloat(p2.salariu))} | ${p2.situatieImobiliara === "primul" ? "Primul imobil" : "Deține proprietate"}\n`;
    }
    body += `\nPARAMETRI CALCUL:\n`;
    body += `Grad îndatorare: ${results.gradIndatorare}% | Avans minim: ${results.avans}% | IRCC: ${p1.ircc}% | Curs EUR: ${curs} RON\n`;
    body += `An construcție: ${p1.anConstructie || "-"} | Clasă energetică: ${p1.clasaEnergetica}\n`;

    // Modul specific — afiseaza detaliile sumei solicitate
    const isSpecific = mode === "specific" && specificCalcDone && parseFloat(sumaSpecifica.sumCredit) > 0;
    if (isSpecific) {
      body += `\nSUMĂ SOLICITATĂ:\n`;
      body += `Sumă credit: ${fmt(parseFloat(sumaSpecifica.sumCredit))}\n`;
      body += `Avans: ${fmt(parseFloat(sumaSpecifica.avans))} | Preț imobil: ${fmt(parseFloat(sumaSpecifica.pretImobil))}\n`;
      if (sumaSpecifica.perioadaLuni) {
        body += `Perioadă dorită: ${sumaSpecifica.perioadaLuni} luni (${(parseFloat(sumaSpecifica.perioadaLuni)/12).toFixed(1)} ani)\n`;
      }
    }

    body += `\n` + `=`.repeat(50) + `\n`;
    const modeLabel = isSpecific ? "ELIGIBILITATE BANCĂ (sumă solicitată + sumă maximă)" : "OFERTE DISPONIBILE (sumă maximă)";
    body += `${modeLabel}\n`;
    body += `Sortare: ${sortLabel} | ${visibleCount} oferte afișate\n`;
    body += `=`.repeat(50) + `\n\n`;

    // Calculeaza bancile enriched pentru modul specific
    let banksForEmail = results.banks;
    if (isSpecific) {
      const scSolicitat = parseFloat(sumaSpecifica.sumCredit) || 0;
      const perioadaSolicitataLuni = parseFloat(sumaSpecifica.perioadaLuni) || 0;
      banksForEmail = results.banks.map(b => {
        let frSolicitat = b.fixedRate;
        if (b.ingTable) {
          const sS = scSolicitat > 400000 ? "mare" : scSolicitat >= 200000 ? "medie" : "mica";
          const aS = b.ingAvansOver40 ? "gte40" : b.ingAvansBetween2040 ? "2040" : "lt20";
          const ingT = {
            A: { mare:{gte40:[1.89,4.79,1.89,5.29,1.89],"2040":[2.09,4.99,2.09,5.49,2.09],lt20:[2.29,5.19,2.29,5.69,2.29]}, medie:{gte40:[2.09,4.99,2.09,5.49,2.09],"2040":[2.29,5.19,2.29,5.69,2.29],lt20:[2.49,5.39,2.49,5.89,2.49]}, mica:{gte40:[2.29,5.19,2.29,5.69,2.49],"2040":[2.49,5.39,2.49,5.89,2.69],lt20:[2.69,5.59,2.69,6.09,2.89]} },
            alta: { mare:{gte40:[2.09,4.99,2.09,5.49,2.09],"2040":[2.29,5.19,2.29,5.69,2.29],lt20:[2.49,5.39,2.49,5.89,2.49]}, medie:{gte40:[2.29,5.19,2.29,5.69,2.29],"2040":[2.49,5.39,2.49,5.89,2.49],lt20:[2.69,5.59,2.69,6.09,2.69]}, mica:{gte40:[2.49,5.39,2.49,5.89,2.49],"2040":[2.69,5.59,2.69,6.09,2.69],lt20:[2.89,5.79,2.89,6.29,2.89]} },
          };
          const cls = b.ingIsA ? "A" : "alta";
          const row = ingT[cls][sS][aS];
          frSolicitat = b.ingFixedYears === 3 ? row[1] : row[3];
        }
        const durataFolosita = perioadaSolicitataLuni > 0 ? Math.min(Math.round(perioadaSolicitataLuni), b.maxDurata * 12) / 12 : b.maxDurata;
        const nSolicitat = Math.round(durataFolosita * 12);
        const fyMatchE = b.dobanda.match(/fix (\d+) ani/);
        const fixedYearsE = fyMatchE ? parseInt(fyMatchE[1]) : 0;
        const comisionAnualE = b.name === "Banca Transilvania" ? 150 : 0;
        const durataAniE = Math.round(nSolicitat/12);
        const rataSolicitata = calcMonthlyPayment(scSolicitat, frSolicitat, nSolicitat);
        const asigSolicitata = b.lifeInsRate > 0 ? scSolicitat * (b.lifeInsRate / 100) : 0;
        const calcSolE = calcTotalPlata(scSolicitat, frSolicitat, fixedYearsE, b.variableRate, durataAniE, b.lifeInsRate, comisionAnualE);
        const totalSolicitat = calcSolE.totalDePlata;
        const costTotalSolicitat = calcSolE.costTotal;
        return { ...b, rataSolicitata, asigSolicitata, totalSolicitat, costTotalSolicitat, scSolicitat, frSolicitat, durataAfisata: durataAniE, incadrat: scSolicitat <= b.sumCredit };
      });
    }

    const topVisible = getSortedBanks(banksForEmail, visibleCount);
    topVisible.forEach((b, i) => {
      body += `${i + 1}. Oferta ${i + 1}\n`;
      body += `   Dobândă: ${b.dobanda} (variabilă: ${b.variableRate.toFixed(2)}%)\n`;
      if (isSpecific) {
        body += `   ── Suma solicitată: ${fmt(b.scSolicitat)} ${b.durataAfisata ? `| ${b.durataAfisata} ani` : ""}\n`;
        body += `      Rată lunară: ${fmt(b.rataSolicitata)}`;
        if (b.asigSolicitata > 0) body += ` + ${fmt(b.asigSolicitata)} asig. = ${fmt(b.rataSolicitata + b.asigSolicitata)} total/lună`;
        body += `\n`;
        body += `      Total de plată: ${fmt(b.totalSolicitat)}\n`;
        if (b.costTotalSolicitat > 0) body += `      Cost total (dob.+asig.): ${fmt(b.costTotalSolicitat)}\n`;
        body += `      Eligibil: ${b.incadrat ? "✅ DA" : "❌ NU (depășit)"}\n`;
        body += `   ── Sumă maximă admisă: ${fmt(b.sumCredit)}\n`;
      }
      body += `   Rată lunară (sumă max.): ${fmt(b.rataLunara)}`;
      if (b.asigurareViata > 0) body += ` + ${fmt(b.asigurareViata)} asig. = ${fmt(b.rataLunara + b.asigurareViata)} total/lună`;
      body += `\n`;
      body += `   Valoare imobil: ${fmt(b.valoareImobil)} | Avans (${b.avans.toFixed(0)}%): ${fmt(b.avansVal)}\n`;
      body += `   Total de plată (${b.maxDurata} ani): ${fmt(b.totalPlata)}\n`;
      if (b.costTotal > 0) body += `   Cost total (dobânzi + asigurare): ${fmt(b.costTotal)}\n`;
      if (b.conditii.length) body += `   Condiții: ${b.conditii.join(" | ")}\n`;
      const det = BANK_DETAILS[b.name];
      if (det && det.items.length) {
        body += `   Costuri & detalii:\n`;
        det.items.forEach(item => { body += `      ${item.icon} ${item.text}\n`; });
      }
      body += `\n`;
    });
    body += `=`.repeat(50) + `\n`;
    body += `Ofertă generată prin creditultau.ro\n\nAcest document are scop exclusiv informativ și nu constituie o ofertă contractuală sau un angajament de finanțare. Parametrii prezentați pot fi modificați în urma procesului de analiză a eligibilității, verificării documentelor și aprobării finale din partea instituției financiare. Parametrii ofertei sunt stabiliți în baza condițiilor existente la data întocmirii prezentei simulări și pot fi modificați ulterior ca urmare a schimbărilor intervenite în politica de creditare, condițiile de piață sau criteriile interne ale finanțatorului.\n`;
    body += `Pentru informații suplimentare: ofertare@creditultau.ro`;
    return body;
  };

  const [emailStatus, setEmailStatus] = useState("");

  const handleSendEmail = async () => {
    if (!results) return;
    if (!p1.email) { setEmailStatus("no_email"); return; }

    setEmailStatus("sending");

    const subject = `Ofertă Credit Ipotecar – creditultau.ro – ${p1.nume || "Client"}`;
    const bodyText = buildEmailBody();

    // Construieste HTML grafic similar cu afisarea din ecran
    const curs = parseFloat(p1.cursEuro) || 5.10;
    const fmtH = (v) => {
      if (!v && v !== 0) return "-";
      if (currency === "EUR") return new Intl.NumberFormat("ro-RO").format(Math.round(v / curs)) + " EUR";
      return new Intl.NumberFormat("ro-RO").format(Math.round(v)) + " RON";
    };
    const bankColorsH = { "BCR":"#e63946","UniCredit Bank":"#c62a2a","BRD":"#16a34a","Banca Transilvania":"#2563eb","Exim Bank":"#7c3aed","ING Bank":"#f97316" };
    const isSpecificH = mode === "specific" && specificCalcDone && parseFloat(sumaSpecifica.sumCredit) > 0;
    const sortLabelH = {dobanda:"Dobândă inițială",suma:"Sumă maximă",rata:"Rată lunară",total:"Total de plată",rataSolicitata:"Rată solicitată",totalSolicitat:"Total solicitat"}[sortBy]||"Dobândă";
    const date = new Date().toLocaleDateString("ro-RO",{day:"2-digit",month:"long",year:"numeric"});

    // Calculeaza bancile enriched pentru email
    let banksEmail = results.banks;
    if (isSpecificH) {
      const scS = parseFloat(sumaSpecifica.sumCredit)||0;
      const perS = parseFloat(sumaSpecifica.perioadaLuni)||0;
      banksEmail = results.banks.map(b => {
        let frS = b.fixedRate;
        const durataF = perS > 0 ? Math.min(Math.round(perS), b.maxDurata*12)/12 : b.maxDurata;
        const nS = Math.round(durataF*12);
        const rataS = calcMonthlyPayment(scS, frS, nS);
        const asigS = b.lifeInsRate > 0 ? scS*(b.lifeInsRate/100) : 0;
        const totalS = (rataS+asigS)*nS;
        return {...b, rataSolicitata:rataS, asigSolicitata:asigS, totalSolicitat:totalS, scSolicitat:scS, frSolicitat:frS, durataAfisata:Math.round(nS/12), incadrat:scS<=b.sumCredit};
      });
    }
    const banksH = [...banksEmail].sort((a,b)=>{
      if(sortBy==="dobanda") return a.fixedRate-b.fixedRate;
      if(sortBy==="suma") return b.sumCredit-a.sumCredit;
      if(sortBy==="rata") return a.rataLunara-b.rataLunara;
      if(sortBy==="total") return a.totalPlata-b.totalPlata;
      if(sortBy==="rataSolicitata") return (a.rataSolicitata||0)-(b.rataSolicitata||0);
      if(sortBy==="totalSolicitat") return (a.totalSolicitat||0)-(b.totalSolicitat||0);
      return 0;
    }).slice(0, visibleCount);

    const banksHtml = banksH.map((b,i) => {
      const col = bankColorsH[b.name]||"#64748b";
      const det = BANK_DETAILS[b.name];
      // Header card: numar + nume banca + dobanda
      const headerHtml = `
        <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:8px;margin-bottom:10px">
          <div>
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
              <span style="background:${col};color:#fff;border-radius:4px;padding:2px 8px;font-size:11px;font-weight:700">#${i+1}</span>
              <span style="font-weight:700;font-size:15px;color:#1e293b">Oferta ${i+1}</span>
            </div>
            <div style="font-size:13px;color:#1e293b;font-weight:600">${b.dobanda}</div>
            <div style="font-size:11px;color:#94a3b8">Dobândă variabilă ulterioară: ${b.variableRate.toFixed(2)}%</div>
          </div>
          <div style="text-align:right">
            <div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em">Rată lunară</div>
            <div style="font-size:20px;font-weight:800;color:${col}">${fmtH(b.rataLunara)}</div>
            ${b.asigurareViata>0?`<div style="font-size:11px;color:#0369a1">+ ${fmtH(b.asigurareViata)} asig./lună</div><div style="font-size:12px;font-weight:700;color:#475569">= ${fmtH(b.rataLunara+b.asigurareViata)} total/lună</div>`:""}
          </div>
        </div>`;

      // Grid date suma maxima (folosit in modul maxim si ca al doilea panou in specific)
      const gridMaxima = `
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;background:#f8fafc;border-radius:8px;padding:10px;margin-bottom:8px">
          <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.04em">Sumă credit max.</div><div style="font-weight:700;font-size:13px;color:${col}">${fmtH(b.sumCredit)}</div></div>
          <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.04em">Valoare imobil</div><div style="font-weight:700;font-size:13px;color:#1e293b">${fmtH(b.valoareImobil)}</div></div>
          <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.04em">Avans (${b.avans.toFixed(0)}%)</div><div style="font-weight:700;font-size:13px;color:#1e293b">${fmtH(b.avansVal)}</div></div>
          <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.04em">Durată max.</div><div style="font-weight:700;font-size:13px;color:#1e293b">${b.maxDurata} ani</div></div>
        </div>
        <div style="background:#f5f3ff;border-radius:7px;padding:8px 12px;border-left:3px solid #7c3aed;margin-bottom:8px">
          <div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px">
            <div>
              <div style="font-size:10px;color:#7c3aed;text-transform:uppercase;font-weight:700;letter-spacing:.04em">Total de plată (${b.maxDurata} ani)</div>
              <div style="font-weight:700;color:#7c3aed;font-size:14px">${fmtH(b.totalPlata)}</div>
            </div>
            ${b.costTotal>0?`<div style="text-align:right"><div style="font-size:10px;color:#9333ea;text-transform:uppercase;font-weight:700;letter-spacing:.04em">Cost total (dob.+asig.)</div><div style="font-weight:700;color:#9333ea;font-size:14px">${fmtH(b.costTotal)}</div></div>`:""}
          </div>
        </div>`;

      // Sectiunea specific: 2 panouri side-by-side (solicitat | maxima)
      const scenariiHtml = isSpecificH ? `
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:8px">
          <!-- Stanga: suma solicitata -->
          <div style="background:${b.incadrat?"#f0fdf4":"#fef2f2"};border:1.5px solid ${b.incadrat?"#86efac":"#fca5a5"};border-radius:8px;padding:10px">
            <div style="font-size:10px;font-weight:700;color:${b.incadrat?"#15803d":"#dc2626"};text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px">
              📋 Sumă solicitată: ${fmtH(b.scSolicitat)} · ${b.durataAfisata||b.maxDurata} ani ${b.incadrat?"✅ Eligibil":"❌ Depășit"}
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px">
              <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase">Rată lunară credit</div><div style="font-weight:700;font-size:13px;color:#1e293b">${fmtH(b.rataSolicitata)}</div></div>
              ${b.asigSolicitata>0?`<div><div style="font-size:10px;color:#0369a1;text-transform:uppercase">Asigurare/lună</div><div style="font-weight:700;font-size:13px;color:#0369a1">${fmtH(b.asigSolicitata)}</div></div><div><div style="font-size:10px;color:#475569;text-transform:uppercase">Total/lună</div><div style="font-weight:700;font-size:13px;color:#475569">${fmtH(b.rataSolicitata+b.asigSolicitata)}</div></div>`:""}
            </div>
            <div style="margin-top:8px;padding-top:6px;border-top:1px solid #e2e8f0">
              <div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:4px">
                <div><div style="font-size:10px;color:#7c3aed;text-transform:uppercase;font-weight:700">Total de plată</div><div style="font-weight:700;color:#7c3aed;font-size:13px">${fmtH(b.totalSolicitat)}</div></div>
                ${b.costTotalSolicitat>0?`<div style="text-align:right"><div style="font-size:10px;color:#9333ea;text-transform:uppercase;font-weight:700">Cost (dob.+asig.)</div><div style="font-weight:700;color:#9333ea;font-size:13px">${fmtH(b.costTotalSolicitat)}</div></div>`:""}
              </div>
            </div>
          </div>
          <!-- Dreapta: suma maxima -->
          <div style="background:#f8fafc;border:1.5px solid #e2e8f0;border-radius:8px;padding:10px">
            <div style="font-size:10px;font-weight:700;color:${col};text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px">
              🏆 Sumă maximă: ${fmtH(b.sumCredit)} · ${b.maxDurata} ani
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px">
              <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase">Rată lunară credit</div><div style="font-weight:700;font-size:13px;color:${col}">${fmtH(b.rataLunara)}</div></div>
              ${b.asigurareViata>0?`<div><div style="font-size:10px;color:#0369a1;text-transform:uppercase">Asigurare/lună</div><div style="font-weight:700;font-size:13px;color:#0369a1">${fmtH(b.asigurareViata)}</div></div><div><div style="font-size:10px;color:#475569;text-transform:uppercase">Total/lună</div><div style="font-weight:700;font-size:13px;color:#475569">${fmtH(b.rataLunara+b.asigurareViata)}</div></div>`:""}
              <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase">Valoare imobil</div><div style="font-weight:700;font-size:13px;color:#1e293b">${fmtH(b.valoareImobil)}</div></div>
              <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase">Avans (${b.avans.toFixed(0)}%)</div><div style="font-weight:700;font-size:13px;color:#1e293b">${fmtH(b.avansVal)}</div></div>
            </div>
            <div style="margin-top:8px;padding-top:6px;border-top:1px solid #e2e8f0">
              <div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:4px">
                <div><div style="font-size:10px;color:#7c3aed;text-transform:uppercase;font-weight:700">Total de plată</div><div style="font-weight:700;color:#7c3aed;font-size:13px">${fmtH(b.totalPlata)}</div></div>
                ${b.costTotal>0?`<div style="text-align:right"><div style="font-size:10px;color:#9333ea;text-transform:uppercase;font-weight:700">Cost (dob.+asig.)</div><div style="font-weight:700;color:#9333ea;font-size:13px">${fmtH(b.costTotal)}</div></div>`:""}
              </div>
            </div>
          </div>
        </div>` : gridMaxima;

      // Conditii + costuri detalii
      const conditiiHtml = b.conditii && b.conditii.length > 0 ?
        `<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:6px">${b.conditii.map(c=>`<span style="background:${col}18;color:${col};border-radius:4px;padding:2px 8px;font-size:11px;font-weight:500">${c}</span>`).join("")}</div>` : "";
      const costuriHtml = det ? `
        <div style="margin-top:6px;padding:8px 10px;background:#f0f9ff;border-radius:6px;border-left:3px solid #1a6de0">
          <div style="font-size:10px;font-weight:700;color:#1a6de0;text-transform:uppercase;margin-bottom:4px">Costuri &amp; detalii</div>
          ${det.items.map(item=>`<div style="font-size:11px;color:#475569;margin-bottom:2px">${item.icon} ${item.text}</div>`).join("")}
        </div>` : "";

      return `<div style="border:2px solid ${col}25;border-left:5px solid ${col};border-radius:10px;padding:14px 16px;margin-bottom:12px;background:#fff">
        ${headerHtml}${scenariiHtml}${conditiiHtml}${costuriHtml}
      </div>`;
    }).join("");

    const bodyHtml = `<div style="font-family:Arial,sans-serif;max-width:700px;margin:0 auto;color:#1e293b;background:#f0f4ff;padding:20px">
      <div style="background:linear-gradient(135deg,#1a6de0,#2563eb);padding:20px 24px;border-radius:12px 12px 0 0">
        <h1 style="color:#fff;margin:0;font-size:20px">🏠 Ofertă Credit Ipotecar</h1>
        <p style="color:#bfdbfe;margin:6px 0 0;font-size:13px">creditultau.ro · ${date} · Afișare: ${currency}${currency==="EUR"?" (1 EUR = "+curs+" RON)":""}</p>
      </div>
      <div style="background:#fff;padding:16px 20px;border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;margin-bottom:16px">
        <div style="font-size:12px;color:#475569;line-height:1.8">
          <strong style="color:#1e293b">${p1.nume||"-"}</strong> · ${p1.telefon||""} · ${p1.email||""}<br/>
          Vârstă: ${p1.varsta||"-"} ani · Salariu: ${fmtH(parseFloat(p1.salariu))} · ${p1.situatieImobiliara==="primul"?"Primul imobil":"Deține proprietate"}
          ${codebitorActive?`<br/><strong style="color:#f97316">Codebitor:</strong> ${p2.nume||"-"} · ${p2.telefon||""} · ${p2.email||""}<br/>Vârstă: ${p2.varsta||"-"} ani · Salariu: ${fmtH(parseFloat(p2.salariu))} · ${p2.situatieImobiliara==="primul"?"Primul imobil":"Deține proprietate"}`:""}
        </div>
        <div style="display:flex;gap:16px;margin-top:10px;padding-top:10px;border-top:1px solid #f1f5f9;flex-wrap:wrap">
          <div style="font-size:11px;color:#64748b">Grad îndatorare: <strong>${results.gradIndatorare}%</strong></div>
          <div style="font-size:11px;color:#64748b">Avans minim: <strong>${results.avans}%</strong></div>
          <div style="font-size:11px;color:#64748b">IRCC: <strong>${p1.ircc}%</strong></div>
          <div style="font-size:11px;color:#64748b">Sortare: <strong>${sortLabelH}</strong></div>
        </div>
        ${isSpecificH?`<div style="margin-top:10px;padding:10px;background:#f0fdf4;border-radius:8px;border:1.5px solid #86efac;font-size:12px">
          <strong>Suma solicitată:</strong> ${fmtH(parseFloat(sumaSpecifica.sumCredit))} · Avans: ${fmtH(parseFloat(sumaSpecifica.avans))} · Preț imobil: ${fmtH(parseFloat(sumaSpecifica.pretImobil))}${sumaSpecifica.perioadaLuni?" · Perioadă: "+sumaSpecifica.perioadaLuni+" luni":""}
        </div>`:""}
      </div>
      <div style="background:#fffbeb;border:1.5px solid #fbbf24;border-left:4px solid #f59e0b;border-radius:10px;padding:14px 16px;margin-bottom:16px">
        <div style="display:flex;gap:10px;align-items:flex-start">
          <span style="font-size:18px;flex-shrink:0">⚠️</span>
          <p style="margin:0;font-size:11px;color:#78350f;line-height:1.7">Acest document are scop exclusiv informativ și nu constituie o ofertă contractuală sau un angajament de finanțare. Parametrii prezentați pot fi modificați în urma procesului de analiză a eligibilității, verificării documentelor și aprobării finale din partea instituției financiare. Parametrii ofertei sunt stabiliți în baza condițiilor existente la data întocmirii prezentei simulări și pot fi modificați ulterior ca urmare a schimbărilor intervenite în politica de creditare, condițiile de piață sau criteriile interne ale finanțatorului.</p>
        </div>
      </div>
      <div style="font-weight:700;font-size:15px;color:#1e293b;margin-bottom:12px">📊 ${isSpecificH?"Eligibilitate per bancă":"Oferte disponibile"} (${banksH.length})</div>
      ${banksHtml}
      <p style="font-size:11px;color:#94a3b8;margin-top:16px;text-align:center">
        Ofertă generată prin <a href="https://www.creditultau.ro" style="color:#1a6de0">creditultau.ro</a><br/><br/><span style="color:#64748b;font-size:10px;line-height:1.6">Acest document are scop exclusiv informativ și nu constituie o ofertă contractuală sau un angajament de finanțare. Parametrii prezentați pot fi modificați în urma procesului de analiză a eligibilității, verificării documentelor și aprobării finale din partea instituției financiare. Parametrii ofertei sunt stabiliți în baza condițiilor existente la data întocmirii prezentei simulări și pot fi modificați ulterior ca urmare a schimbărilor intervenite în politica de creditare, condițiile de piață sau criteriile interne ale finanțatorului.</span>
      </p>
    </div>`;

    try {
      const res = await fetch("https://www.creditultau.ro/wp-json/creditultau/v1/trimite-oferta", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: p1.email,
          cc_extra: codebitorActive && p2.email ? p2.email : "",
          subject,
          body: bodyHtml,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setEmailStatus("ok");
        setTimeout(() => setEmailStatus(""), 5000);
      } else {
        setEmailStatus("err");
        setTimeout(() => setEmailStatus(""), 5000);
      }
    } catch (e) {
      setEmailStatus("err");
      setTimeout(() => setEmailStatus(""), 5000);
    }
  };

    const handleDownloadPDF = () => {
    if (!results) return;
    const ircc = p1.ircc;
    const sorted = [...results.banks].sort((a, b) => {
      if (sortBy === "dobanda") return a.fixedRate - b.fixedRate;
      if (sortBy === "suma") return b.sumCredit - a.sumCredit;
      if (sortBy === "rata") return a.rataLunara - b.rataLunara;
      if (sortBy === "total") return a.totalPlata - b.totalPlata;
      return 0;
    }).slice(0, visibleCount);
    const sortLabel = { dobanda: "Dobândă inițială", suma: "Sumă maximă", rata: "Rată lunară", total: "Total de plată" }[sortBy];
    const bankColors = { "BCR": "#e63946", "UniCredit Bank": "#c62a2a", "BRD": "#16a34a", "Banca Transilvania": "#2563eb", "Exim Bank": "#7c3aed", "ING Bank": "#f97316" };
    const fmt = (v) => v ? new Intl.NumberFormat("ro-RO").format(Math.round(v)) + " RON" : "-";

    const banksHTML = sorted.map((b, i) => {
      const color = bankColors[b.name] || "#64748b";
      const conditiiHTML = b.conditii.map(c => `<span style="background:${color}18;color:${color};border-radius:4px;padding:2px 8px;font-size:11px;font-weight:600;margin:2px 2px 0 0;display:inline-block">${c}</span>`).join("");
      return `
        <div style="border:2px solid ${color}25;border-left:5px solid ${color};border-radius:10px;padding:14px 16px;margin-bottom:12px;background:#fff">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:8px">
            <div>
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
                <span style="background:${color};color:#fff;border-radius:4px;padding:2px 9px;font-size:11px;font-weight:700">#${i+1}</span>
                <span style="font-weight:700;font-size:15px;color:#1e293b">Oferta ${i+1}</span>
              </div>
              <div style="font-size:13px;color:#1e293b;font-weight:600">${b.dobanda}</div>
              <div style="font-size:11px;color:#94a3b8">Dobândă variabilă ulterioară: ${b.variableRate.toFixed(2)}%</div>
            </div>
            <div style="text-align:right">
              <div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em">Rată lunară</div>
              <div style="font-size:22px;font-weight:800;color:${color}">${fmt(b.rataLunara)}</div>
              ${b.asigurareViata > 0 ? `<div style="font-size:11px;color:#0369a1">+ ${fmt(b.asigurareViata)} asig.</div><div style="font-size:12px;font-weight:700;color:#475569">= ${fmt(b.rataLunara + b.asigurareViata)} total/lună</div>` : ""}
            </div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:12px;background:#f8fafc;border-radius:8px;padding:10px 12px">
            <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase">Sumă credit</div><div style="font-weight:700;color:#1e293b;font-size:13px">${fmt(b.sumCredit)}</div></div>
            <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase">Valoare imobil</div><div style="font-weight:700;color:#1e293b;font-size:13px">${fmt(b.valoareImobil)}</div></div>
            <div><div style="font-size:10px;color:#94a3b8;text-transform:uppercase">Avans (${b.avans.toFixed(0)}%)</div><div style="font-weight:700;color:#1e293b;font-size:13px">${fmt(b.avansVal)}</div></div>
            <div><div style="font-size:10px;color:${b.asigurareViata > 0 ? '#0369a1' : '#94a3b8'};text-transform:uppercase;font-weight:${b.asigurareViata > 0 ? '700' : '400'}">Asigurare viață/lună${b.lifeInsRate > 0 ? ' (' + b.lifeInsRate + '%)' : ''}</div><div style="font-weight:700;color:${b.asigurareViata > 0 ? '#0369a1' : '#16a34a'};font-size:13px">${b.asigurareViata > 0 ? fmt(b.asigurareViata) : 'Nu se aplică'}</div></div>
          </div>
          <div style="margin-top:8px;background:#f5f3ff;border-radius:6px;padding:8px 12px;border-left:3px solid #7c3aed">
            <div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:8px"><div><div style="font-size:10px;color:#7c3aed;text-transform:uppercase;font-weight:700;letter-spacing:.05em">Total de plată (${b.maxDurata} ani)</div><div style="font-weight:800;color:#7c3aed;font-size:15px">${fmt(b.totalPlata)}</div></div>${b.costTotal>0?`<div style="text-align:right"><div style="font-size:10px;color:#9333ea;text-transform:uppercase;font-weight:700;letter-spacing:.05em">Cost total (dob.+asig.)</div><div style="font-weight:800;color:#9333ea;font-size:15px">${fmt(b.costTotal)}</div></div>`:""}</div>
          </div>
          ${conditiiHTML ? `<div style="margin-top:8px">${conditiiHTML}</div>` : ""}
          ${b.mentiuneImobil ? `<div style="margin-top:6px;font-size:11px;color:#7c3aed;background:#f5f3ff;border-radius:4px;padding:4px 8px">ℹ️ ${b.mentiuneImobil}</div>` : ""}
          ${BANK_DETAILS[b.name] ? `<div style="margin-top:8px;padding:8px 10px;background:#f0f9ff;border-radius:6px;border-left:3px solid #1a6de0"><div style="font-size:10px;font-weight:700;color:#1a6de0;text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px">Costuri &amp; detalii</div>${BANK_DETAILS[b.name].items.map(item => `<div style="font-size:11px;color:#475569;margin-bottom:2px">${item.icon} ${item.text}</div>`).join("")}</div>` : ""}
        </div>`;
    }).join("");

    const date = new Date().toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" });
    const html = `<!DOCTYPE html>
<html lang="ro">
<head>
<meta charset="UTF-8">
<title>Ofertă Credit Ipotecar – creditultau.ro</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #f0f4ff; color: #1e293b; }
  .page { max-width: 800px; margin: 0 auto; padding: 32px 24px; background: #f0f4ff; }
  @media print {
    body { background: #fff; }
    .page { padding: 0; max-width: 100%; }
    .no-print { display: none !important; }
    @page { margin: 15mm 12mm; size: A4; }
  }
</style>
</head>
<body>
<div class="page">
  <!-- Header -->
  <div style="background:#fff;border-radius:14px;padding:20px 24px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:center;border:2px solid #e2e8f0">
    <div style="display:flex;align-items:center;gap:12px">
      <div style="width:46px;height:46px;background:linear-gradient(135deg,#1a6de0,#2563eb);border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:22px">🏠</div>
      <div>
        <div style="font-weight:800;font-size:20px;color:#1e293b">creditul<span style="color:#1a6de0">tău</span><span style="color:#94a3b8;font-size:14px">.ro</span></div>
        <div style="font-size:11px;color:#94a3b8;font-weight:500">Ofertă Credit Ipotecar</div>
      </div>
    </div>
    <div style="text-align:right;font-size:12px;color:#64748b">
      <div>Data: <strong>${date}</strong></div>
      <div>IRCC: <strong>${ircc}%</strong></div>
    </div>
  </div>

  <!-- Participanti -->
  <div style="background:#fff;border-radius:12px;padding:16px 20px;margin-bottom:16px;border:2px solid #e2e8f0">
    <div style="font-weight:700;font-size:13px;color:#64748b;text-transform:uppercase;letter-spacing:.05em;margin-bottom:12px">Participanți</div>
    <div style="display:grid;grid-template-columns:${codebitorActive ? "1fr 1fr" : "1fr"};gap:16px">
      <div style="border-left:4px solid #1a6de0;padding-left:12px">
        <div style="font-size:10px;color:#94a3b8;text-transform:uppercase;font-weight:600;margin-bottom:4px">Solicitant Principal</div>
        <div style="font-weight:700;font-size:14px;color:#1e293b">${p1.nume || "–"}</div>
        <div style="font-size:12px;color:#475569;margin-top:2px">${p1.telefon}${p1.email ? " · " + p1.email : ""}</div>
        <div style="font-size:12px;color:#475569">${p1.varsta ? p1.varsta + " ani" : ""} · Salariu: ${fmt(parseFloat(p1.salariu))} · ${p1.situatieImobiliara === "primul" ? "Primul imobil" : "Deține proprietate"}</div>
      </div>
      ${codebitorActive ? `<div style="border-left:4px solid #f97316;padding-left:12px">
        <div style="font-size:10px;color:#94a3b8;text-transform:uppercase;font-weight:600;margin-bottom:4px">Codebitor</div>
        <div style="font-weight:700;font-size:14px;color:#1e293b">${p2.nume || "–"}</div>
        <div style="font-size:12px;color:#475569;margin-top:2px">${p2.telefon}${p2.email ? " · " + p2.email : ""}</div>
        <div style="font-size:12px;color:#475569">${p2.varsta ? p2.varsta + " ani" : ""} · Salariu: ${fmt(parseFloat(p2.salariu))} · ${p2.situatieImobiliara === "primul" ? "Primul imobil" : "Deține proprietate"}</div>
      </div>` : ""}
    </div>
  </div>

  <!-- Parametri -->
  <div style="background:#fff;border-radius:12px;padding:14px 20px;margin-bottom:20px;border:2px solid #e2e8f0;display:flex;gap:24px;flex-wrap:wrap">
    <div><span style="font-size:11px;color:#94a3b8;text-transform:uppercase;font-weight:600">Grad îndatorare</span><div style="font-weight:700;font-size:16px;color:#1a6de0">${results.gradIndatorare}%</div></div>
    <div><span style="font-size:11px;color:#94a3b8;text-transform:uppercase;font-weight:600">Avans minim</span><div style="font-weight:700;font-size:16px;color:#1a6de0">${results.avans}%</div></div>
    <div><span style="font-size:11px;color:#94a3b8;text-transform:uppercase;font-weight:600">IRCC</span><div style="font-weight:700;font-size:16px;color:#1a6de0">${ircc}%</div></div>
    <div><span style="font-size:11px;color:#94a3b8;text-transform:uppercase;font-weight:600">Sortare oferte</span><div style="font-weight:700;font-size:14px;color:#475569">${sortLabel}</div></div>
  </div>

  <!-- Oferte -->
  <div style="font-weight:800;font-size:16px;color:#1e293b;margin-bottom:12px">📊 Oferte disponibile (${sorted.length})</div>
  ${banksHTML}

  <!-- Footer -->
  <div style="margin-top:24px;text-align:center;font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:14px">
    Ofertă generată prin <strong>creditultau.ro</strong> · ${date}<br/><br/><span style="font-size:10px;color:#64748b;line-height:1.6">Acest document are scop exclusiv informativ și nu constituie o ofertă contractuală sau un angajament de finanțare. Parametrii prezentați pot fi modificați în urma procesului de analiză a eligibilității, verificării documentelor și aprobării finale din partea instituției financiare. Parametrii ofertei sunt stabiliți în baza condițiilor existente la data întocmirii prezentei simulări și pot fi modificați ulterior ca urmare a schimbărilor intervenite în politica de creditare, condițiile de piață sau criteriile interne ale finanțatorului.</span>
  </div>

  <div class="no-print" style="margin-top:20px;text-align:center">
    <button onclick="window.print()" style="background:linear-gradient(135deg,#1a6de0,#2563eb);color:#fff;border:none;border-radius:10px;padding:12px 32px;font-size:15px;font-weight:700;cursor:pointer">🖨️ Salvează / Printează PDF</button>
  </div>
</div>
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (win) win.focus();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  return (
    <div style={{
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      background: "linear-gradient(135deg, #f0f4ff 0%, #fafafa 100%)",
      minHeight: "100vh", padding: "20px 16px",
      overflowX: "hidden",
    }}>


      {/* Main column */}
      <div style={{ maxWidth: 680, margin: "0 auto", position: "relative" }}>

        <MainForm
          p1={p1} setP1={handleP1Change}
          p2={p2} setP2={handleP2Change}
          codebitorActive={codebitorActive}
          setCodebitorActive={handleCodebitorChange}
          showMissing={showMissing}
        />

        {/* Validation error */}
        {showMissing && !canCalculate && (
          <div style={{
            background: "#fef2f2", border: "1.5px solid #fca5a5",
            borderRadius: 9, padding: "10px 14px", marginTop: 12,
            fontSize: 12, color: "#991b1b",
          }}>
            ⛔ Completați toate câmpurile obligatorii (marcate cu <strong>*</strong>) înainte de calculare.
          </div>
        )}

        {/* Acorduri obligatorii */}
        {(() => {
          const allAccepted = tc1 && tc2 && tc3 && tc4;
          const checkboxes = [
            {
              id: "tc1", checked: tc1, set: setTc1,
              label: <>Confirm că am citit{" "}
                <a href="https://www.creditultau.ro/politici-web/" target="_blank" rel="noopener noreferrer"
                  style={{ color: "#1a6de0", fontWeight: 600, textDecoration: "underline" }}>
                  termenii și condițiile creditultau.ro
                </a>
              </>,
            },
            {
              id: "tc2", checked: tc2, set: setTc2,
              label: <>Am citit și am înțeles{" "}
                <a href="https://www.creditultau.ro/gdpr/" target="_blank" rel="noopener noreferrer"
                  style={{ color: "#1a6de0", fontWeight: 600, textDecoration: "underline" }}>
                  politica privind protecția datelor cu caracter personal (GDPR)
                </a>
              </>,
            },
            {
              id: "tc3", checked: tc3, set: setTc3,
              label: <>Solicit primirea unei simulări de ofertă pe adresa de email menționată în formular și am luat la cunoștință că aceasta are caracter pur informativ și nu reprezintă o ofertă fermă de creditare. Condițiile finale pot diferi în funcție de analiza instituției financiare</>,
            },
            {
              id: "tc4", checked: tc4, set: setTc4,
              label: <>Confirm că am peste 18 ani</>,
            },
          ];
          return (
            <div style={{
              background: "#fff", borderRadius: 10, padding: "14px 16px",
              border: `1.5px solid ${allAccepted ? "#86efac" : showMissing ? "#fca5a5" : "#e2e8f0"}`,
              marginTop: 16, marginBottom: 4,
              transition: "border-color 0.2s",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
                Acorduri obligatorii <span style={{ color: "#ef4444" }}>*</span>
              </div>
              {checkboxes.map(({ id, checked, set, label }) => (
                <label key={id} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 10 }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => set(e.target.checked)}
                    style={{ width: 17, height: 17, marginTop: 2, accentColor: "#1a6de0", cursor: "pointer", flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 12, color: "#1e293b", lineHeight: 1.6 }}>
                    {label}
                  </span>
                </label>
              ))}
              {showMissing && !allAccepted && (
                <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4, paddingTop: 8, borderTop: "1px solid #fee2e2" }}>
                  ⚠️ Trebuie să bifați toate acordurile de mai sus pentru a continua.
                </div>
              )}
            </div>
          );
        })()}

        {/* Mode buttons */}
        <div style={{ margin: "16px 0" }}>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => {
                if (!canCalculate) { setShowMissing(true); return; }
                setMode("maxim");
                setSpecificCalcDone(false);
                setVisibleCount(5);
                const r = computeResults(p1, p2, codebitorActive);
                setResults(r);
                setCalculated(true);
                setShowMissing(false);
              }}
              style={{
                background: canCalculate && mode === "maxim"
                  ? "linear-gradient(135deg, #1a6de0, #2563eb)"
                  : canCalculate ? "#f8fafc" : "#cbd5e1",
                color: canCalculate && mode === "maxim" ? "#fff" : canCalculate ? "#1a6de0" : "#94a3b8",
                border: canCalculate && mode !== "maxim" ? "2px solid #1a6de0" : "2px solid transparent",
                borderRadius: 10, padding: "13px 28px", fontSize: 15, fontWeight: 700,
                cursor: canCalculate ? "pointer" : "not-allowed",
                boxShadow: canCalculate && mode === "maxim" ? "0 4px 14px #1a6de040" : "none",
                transition: "all 0.2s",
              }}
            >
              🔍 Calculează Suma Maximă
            </button>
            <button
              onClick={() => {
                if (!canCalculate) { setShowMissing(true); return; }
                setMode("specific");
                setSpecificCalcDone(false);
                setVisibleCount(5);
                const r = computeResults(p1, p2, codebitorActive);
                setResults(r);
                setCalculated(true);
                setShowMissing(false);
                // Pre-fill avans based on property status
                const primuImobil = p1.situatieImobiliara === "primul" || (codebitorActive && p2.situatieImobiliara === "primul");
                const avansMinPct = primuImobil ? 15 : 25;
                setSumaSpecifica({ sumCredit: "", avans: "", pretImobil: "", perioadaLuni: "", _avansMinPct: avansMinPct });
              }}
              style={{
                background: canCalculate && mode === "specific"
                  ? "linear-gradient(135deg, #059669, #047857)"
                  : canCalculate ? "#f8fafc" : "#cbd5e1",
                color: canCalculate && mode === "specific" ? "#fff" : canCalculate ? "#059669" : "#94a3b8",
                border: canCalculate && mode !== "specific" ? "2px solid #059669" : "2px solid transparent",
                borderRadius: 10, padding: "13px 28px", fontSize: 15, fontWeight: 700,
                cursor: canCalculate ? "pointer" : "not-allowed",
                boxShadow: canCalculate && mode === "specific" ? "0 4px 14px #05966930" : "none",
                transition: "all 0.2s",
              }}
            >
              💡 Știu câți bani am nevoie
            </button>
          </div>
          {!canCalculate && (
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 8, textAlign: "center" }}>
              Completați toate câmpurile marcate cu <strong>*</strong> pentru a continua
            </div>
          )}
        </div>

        {/* Specific amount panel — sticky */}
        {mode === "specific" && results && (
          <div style={{
            position: "sticky", top: 0, zIndex: 40,
            background: "#f0fdf4", border: "2px solid #86efac",
            borderRadius: 12, padding: "14px 16px", marginBottom: 12,
            boxShadow: "0 3px 12px rgba(5,150,105,0.12)",
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#065f46", marginBottom: 10 }}>
              💡 Suma dorită
            </div>
            {/* 4 campuri pe un rand: sumCredit + perioadaLuni (bold) + avans + pretImobil */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
              {/* Suma credit — primar, bold */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#14532d", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Sumă credit *
                </label>
                <div style={{ position: "relative" }}>
                  <input type="number" value={sumaSpecifica.sumCredit}
                    onChange={e => updateSumaSpecifica("sumCredit", e.target.value)}
                    placeholder="ex: 250000"
                    style={{ width: "100%", padding: "8px 34px 8px 10px", border: "2px solid #4ade80", borderRadius: 7, fontSize: 13, fontWeight: 600, background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box" }} />
                  <span style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", color: "#16a34a", fontSize: 11, fontWeight: 600 }}>RON</span>
                </div>
                {currency === "EUR" && sumaSpecifica.sumCredit && (
                  <div style={{ fontSize: 10, color: "#0369a1", marginTop: 2 }}>
                    ≈ {new Intl.NumberFormat("ro-RO").format(Math.round(parseFloat(sumaSpecifica.sumCredit) / (parseFloat(p1.cursEuro)||5.10)))} EUR
                  </div>
                )}
              </div>
              {/* Perioada — primar, bold */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#14532d", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Perioadă
                </label>
                <div style={{ position: "relative" }}>
                  <input type="number" value={sumaSpecifica.perioadaLuni}
                    onChange={e => { setSumaSpecifica(prev => ({ ...prev, perioadaLuni: e.target.value })); setSpecificCalcDone(false); }}
                    placeholder="ex: 360"
                    min="12" max="420"
                    style={{ width: "100%", padding: "8px 34px 8px 10px", border: "2px solid #4ade80", borderRadius: 7, fontSize: 13, fontWeight: 600, background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box" }} />
                  <span style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", color: "#16a34a", fontSize: 11, fontWeight: 600 }}>luni</span>
                </div>
                {sumaSpecifica.perioadaLuni && parseFloat(sumaSpecifica.perioadaLuni) > 0 && (
                  <div style={{ fontSize: 10, color: "#15803d", marginTop: 2, fontWeight: 600 }}>
                    ({(parseFloat(sumaSpecifica.perioadaLuni) / 12).toFixed(1)} ani)
                  </div>
                )}
              </div>
              {/* Avans — secundar, normal */}
              <div>
                <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: "#065f46", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Avans ({results.avans}% min.)
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type="number"
                    value={avansRaw !== "" ? avansRaw : sumaSpecifica.avans}
                    onChange={e => setAvansRaw(e.target.value)}
                    onFocus={() => setAvansRaw(sumaSpecifica.avans)}
                    onBlur={e => {
                      const v = e.target.value;
                      setAvansRaw("");
                      if (v !== "") updateSumaSpecifica("avans", v);
                    }}
                    placeholder={`min. ${results.avans}%`}
                    style={{ width: "100%", padding: "7px 30px 7px 8px", border: "1.5px solid #86efac", borderRadius: 6, fontSize: 12, background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box" }} />
                  <span style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 10 }}>RON</span>
                </div>
                {sumaSpecifica.avans && sumaSpecifica.pretImobil && parseFloat(sumaSpecifica.pretImobil) > 0 && (
                  <div style={{ fontSize: 10, color: "#059669", marginTop: 2 }}>
                    {(parseFloat(sumaSpecifica.avans) / parseFloat(sumaSpecifica.pretImobil) * 100).toFixed(1)}% din preț
                  </div>
                )}
              </div>
              {/* Pret imobil — secundar, normal */}
              <div>
                <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: "#065f46", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Preț imobil
                </label>
                <div style={{ position: "relative" }}>
                  <input type="number" value={sumaSpecifica.pretImobil}
                    onChange={e => updateSumaSpecifica("pretImobil", e.target.value)}
                    placeholder="credit + avans"
                    style={{ width: "100%", padding: "7px 30px 7px 8px", border: "1.5px solid #86efac", borderRadius: 6, fontSize: 12, background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box" }} />
                  <span style={{ position: "absolute", right: 5, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 10 }}>RON</span>
                </div>
              </div>
            </div>
            {/* Buton verificare */}
            <div style={{ textAlign: "center" }}>
              <button
                onClick={computeSpecificResults}
                disabled={!sumaSpecifica.sumCredit}
                style={{
                  background: sumaSpecifica.sumCredit ? "linear-gradient(135deg, #059669, #047857)" : "#cbd5e1",
                  color: "#fff", border: "none", borderRadius: 8, padding: "9px 32px",
                  fontSize: 13, fontWeight: 700, cursor: sumaSpecifica.sumCredit ? "pointer" : "not-allowed",
                  boxShadow: sumaSpecifica.sumCredit ? "0 3px 10px #05966930" : "none",
                }}
              >
                ✅ Verifică eligibilitatea
              </button>
            </div>
          </div>
        )}

        {/* Resetat - arata mesaj */}
        {!calculated && mode !== "maxim" && canCalculate && (
          <div style={{
            background: "#fef9c3", border: "1.5px solid #fde047",
            borderRadius: 9, padding: "10px 14px", marginTop: 0, marginBottom: 10,
            fontSize: 12, color: "#713f12", textAlign: "center",
          }}>
            ⚠️ Parametrii au fost modificați. Apăsați din nou butonul pentru a recalcula ofertele.
          </div>
        )}

        {/* Results */}
        {calculated && results && (
          <div style={{
            background: "#fffbeb", border: "1.5px solid #fbbf24",
            borderRadius: 10, padding: "14px 16px", marginBottom: 12,
            borderLeft: "4px solid #f59e0b",
          }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: 12, color: "#78350f", lineHeight: 1.7 }}>
                Acest document are scop exclusiv informativ și nu constituie o ofertă contractuală sau un angajament de finanțare. Parametrii prezentați pot fi modificați în urma procesului de analiză a eligibilității, verificării documentelor și aprobării finale din partea instituției financiare. Parametrii ofertei sunt stabiliți în baza condițiilor existente la data întocmirii prezentei simulări și pot fi modificați ulterior ca urmare a schimbărilor intervenite în politica de creditare, condițiile de piață sau criteriile interne ale finanțatorului.
              </p>
            </div>
          </div>
        )}
        {calculated && results && (
          <div style={{
            background: "#fff", borderRadius: 14, padding: 0,
            boxShadow: "0 2px 16px #00000010", marginTop: 10,
          }}>
            {/* Sticky header */}
            <div style={{ position: "sticky", top: mode === "specific" && results ? 140 : 0, zIndex: 39, background: "#fff", borderRadius: "14px 14px 0 0", padding: "14px 20px 12px", borderBottom: "1.5px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 20 }}>📊</span>
                <span style={{ fontWeight: 800, fontSize: 17, color: "#1e293b" }}>
                  {mode === "specific" && specificCalcDone ? "Eligibilitate per bancă" : "Oferte disponibile"}
                </span>
                <span style={{ fontSize: 12, color: "#94a3b8", background: "#f8fafc", borderRadius: 5, padding: "2px 8px" }}>
                  Grad: {results.gradIndatorare}% | Avans: {results.avans}%
                </span>

              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 2 }}>Sortare:</span>
                {(mode === "specific" && specificCalcDone ? [
                  { key: "dobanda", label: "📉 Dobândă inițială" },
                  { key: "suma", label: "💰 Sumă maximă" },
                  { key: "rataSolicitata", label: "📆 Rată sumă solicitată" },
                  { key: "totalSolicitat", label: "🧾 Total sumă solicitată" },
                ] : [
                  { key: "dobanda", label: "📉 Dobândă inițială" },
                  { key: "suma", label: "💰 Sumă maximă" },
                  { key: "rata", label: "📆 Rată lunară" },
                  { key: "total", label: "🧾 Total de plată" },
                ]).map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => { setSortBy(key); setVisibleCount(5); }}
                    style={{
                      padding: "6px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                      border: `2px solid ${sortBy === key ? "#1a6de0" : "#e2e8f0"}`,
                      background: sortBy === key ? "#eff6ff" : "#f8fafc",
                      color: sortBy === key ? "#1a6de0" : "#64748b",
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    {label}
                  </button>
                ))}
                {/* Lacăt discret consilier */}
                <button
                  onClick={() => showBankNames ? setShowBankNames(false) : setShowLockModal(true)}
                  title={showBankNames ? "Ascunde detalii" : "Acces consilier"}
                  style={{
                    padding: "4px 8px", borderRadius: 6, fontSize: 14,
                    border: `1.5px solid ${showBankNames ? "#fca5a5" : "#e2e8f0"}`,
                    background: showBankNames ? "#fef2f2" : "#f8fafc",
                    color: showBankNames ? "#dc2626" : "#94a3b8",
                    cursor: "pointer", lineHeight: 1, flexShrink: 0,
                  }}
                >
                  {showBankNames ? "🔓" : "🔒"}
                </button>
                {/* Modal cod acces */}
                {showLockModal && (
                  <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
                    zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
                  }} onClick={() => { setShowLockModal(false); setLockInput(""); setLockError(false); }}>
                    <div style={{
                      background: "#fff", borderRadius: 14, padding: "28px 28px 24px",
                      maxWidth: 340, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                      textAlign: "center",
                    }} onClick={e => e.stopPropagation()}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 6 }}>
                        Acces restricționat
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6, marginBottom: 16 }}>
                        Contactează-ne pentru a primi oferta finală și codul de acces.
                      </div>
                      <input
                        type="password"
                        placeholder="Introdu codul de acces"
                        value={lockInput}
                        onChange={e => { setLockInput(e.target.value); setLockError(false); }}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            if (lockInput === "creditultau2026") {
                              setShowBankNames(true); setShowLockModal(false); setLockInput(""); setLockError(false);
                            } else { setLockError(true); }
                          }
                        }}
                        style={{
                          width: "100%", padding: "9px 12px", border: `1.5px solid ${lockError ? "#fca5a5" : "#e2e8f0"}`,
                          borderRadius: 8, fontSize: 13, outline: "none", boxSizing: "border-box",
                          background: lockError ? "#fef2f2" : "#fff", marginBottom: lockError ? 6 : 12,
                        }}
                        autoFocus
                      />
                      {lockError && <div style={{ fontSize: 11, color: "#dc2626", marginBottom: 10 }}>Cod incorect. Încearcă din nou.</div>}
                      <button
                        onClick={() => {
                          if (lockInput === "creditultau2026") {
                            setShowBankNames(true); setShowLockModal(false); setLockInput(""); setLockError(false);
                          } else { setLockError(true); }
                        }}
                        style={{
                          width: "100%", padding: "10px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                          background: "linear-gradient(135deg, #1a6de0, #2563eb)", color: "#fff",
                          border: "none", cursor: "pointer",
                        }}
                      >
                        Accesează
                      </button>
                    </div>
                  </div>
                )}
                {/* EUR toggle — pe aceeasi linie cu sortarile */}
                <button
                  onClick={() => setCurrency(prev => prev === "RON" ? "EUR" : "RON")}
                  style={{
                    marginLeft: "auto", padding: "5px 13px", borderRadius: 7, fontSize: 12, fontWeight: 700,
                    border: `2px solid ${currency === "EUR" ? "#0369a1" : "#e2e8f0"}`,
                    background: currency === "EUR" ? "#f0f9ff" : "#f8fafc",
                    color: currency === "EUR" ? "#0369a1" : "#64748b",
                    cursor: "pointer", transition: "all 0.15s",
                    display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
                  }}
                >
                  {currency === "EUR" ? "💱 Vezi în RON" : "💱 Vezi în EUR"}
                </button>
              </div>
            </div>{/* end sticky header */}

            {/* Scrollable content */}
            <div style={{ padding: "16px 20px 4px" }}>

            {results.banks.length === 0 ? (
              <div style={{ textAlign: "center", color: "#ef4444", padding: 20 }}>
                ⚠️ Nu s-a putut calcula nicio ofertă. Verificați datele introduse.
              </div>
            ) : (
              (() => {
                const scSolicitat = parseFloat(sumaSpecifica.sumCredit) || 0;
                const perioadaSolicitataLuni = parseFloat(sumaSpecifica.perioadaLuni) || 0;
                // Enrich banks with specific-amount data
                const banksEnriched = results.banks.map(b => {
                  if (mode === "specific" && specificCalcDone && scSolicitat > 0) {
                    // Daca s-a specificat o perioada, o folosim (limitat la maxDurata bancii)
                    const durataFolosita = perioadaSolicitataLuni > 0
                      ? Math.min(Math.round(perioadaSolicitataLuni), b.maxDurata * 12) / 12
                      : b.maxDurata;
                    const nSolicitat = Math.round(durataFolosita * 12);
                    // Pentru ING, recalculeaza dobanda corespunzatoare sumei solicitate
                    let frSolicitat = b.fixedRate;
                    if (b.ingTable) {
                      const sS = scSolicitat > 400000 ? "mare" : scSolicitat >= 200000 ? "medie" : "mica";
                      const aS = b.ingAvansOver40 ? "gte40" : b.ingAvansBetween2040 ? "2040" : "lt20";
                      const ingTableData = {
                        A: {
                          mare:  { gte40:[1.89,4.79,1.89,5.29,1.89], "2040":[2.09,4.99,2.09,5.49,2.09], lt20:[2.29,5.19,2.29,5.69,2.29] },
                          medie: { gte40:[2.09,4.99,2.09,5.49,2.09], "2040":[2.29,5.19,2.29,5.69,2.29], lt20:[2.49,5.39,2.49,5.89,2.49] },
                          mica:  { gte40:[2.29,5.19,2.29,5.69,2.49], "2040":[2.49,5.39,2.49,5.89,2.69], lt20:[2.69,5.59,2.69,6.09,2.89] },
                        },
                        alta: {
                          mare:  { gte40:[2.09,4.99,2.09,5.49,2.09], "2040":[2.29,5.19,2.29,5.69,2.29], lt20:[2.49,5.39,2.49,5.89,2.49] },
                          medie: { gte40:[2.29,5.19,2.29,5.69,2.29], "2040":[2.49,5.39,2.49,5.89,2.49], lt20:[2.69,5.59,2.69,6.09,2.69] },
                          mica:  { gte40:[2.49,5.39,2.49,5.89,2.49], "2040":[2.69,5.59,2.69,6.09,2.69], lt20:[2.89,5.79,2.89,6.29,2.89] },
                        },
                      };
                      const cls = b.ingIsA ? "A" : "alta";
                      const row = ingTableData[cls][sS][aS];
                      // row: [calcRateSpread, fr3, vs3, fr5, vs5]
                      frSolicitat = b.ingFixedYears === 3 ? row[1] : row[3];
                    }
                    const fyMatchSol = b.dobanda.match(/fix (\d+) ani/);
                    const fixedYearsSol = fyMatchSol ? parseInt(fyMatchSol[1]) : 0;
                    const comisionAnualSol = b.name === "Banca Transilvania" ? 150 : 0;
                    const durataAniiSol = Math.round(nSolicitat / 12);
                    // Rata calculata pe durata reala a clientului (metodologie corecta)
                    const rataSolicitata = calcMonthlyPayment(scSolicitat, frSolicitat, nSolicitat);
                    const asigSolicitata = b.lifeInsRate > 0 ? scSolicitat * (b.lifeInsRate / 100) : 0;
                    const calcSol = calcTotalPlata(scSolicitat, frSolicitat, fixedYearsSol, b.variableRate, durataAniiSol, b.lifeInsRate, comisionAnualSol);
                    const totalSolicitat = calcSol.totalDePlata;
                    const costTotalSolicitat = calcSol.costTotal;
                    const incadrat = scSolicitat <= b.sumCredit;
                    const durataAfisata = durataAniiSol;
                    return { ...b, rataSolicitata, asigSolicitata, totalSolicitat, costTotalSolicitat, incadrat, scSolicitat, frSolicitat, durataAfisata };
                  }
                  return b;
                });

                const sorted = [...banksEnriched].sort((a, b) => {
                  if (sortBy === "dobanda") return a.fixedRate - b.fixedRate;
                  if (sortBy === "suma") return b.sumCredit - a.sumCredit;
                  if (sortBy === "rata") return a.rataLunara - b.rataLunara;
                  if (sortBy === "total") return a.totalPlata - b.totalPlata;
                  if (sortBy === "rataSolicitata") return (a.rataSolicitata||0) - (b.rataSolicitata||0);
                  if (sortBy === "totalSolicitat") return (a.totalSolicitat||0) - (b.totalSolicitat||0);
                  return 0;
                });
                const visible = sorted.slice(0, visibleCount);
                const total = sorted.length;
                const canShowMore = visibleCount < total;
                const canShowLess = visibleCount > 5;
                return (
                  <>
                    {visible.map((b, i) => (
                      <BankCard
                        key={`${b.name}-${b.dobanda}`}
                        bank={b}
                        index={i}
                        showSpecific={mode === "specific" && specificCalcDone && scSolicitat > 0}
                        currency={currency}
                        cursEuro={p1.cursEuro}
                        showBankName={showBankNames}
                      />
                    ))}
                    {/* Butoane paginare */}
                    {(canShowMore || canShowLess) && (
                      <div style={{ display: "flex", gap: 8, justifyContent: "center", margin: "12px 0 4px" }}>
                        {canShowLess && (
                          <button
                            onClick={() => setVisibleCount(v => Math.max(5, v - 5))}
                            style={{
                              padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                              border: "2px solid #e2e8f0", background: "#f8fafc", color: "#64748b",
                              cursor: "pointer", transition: "all 0.15s",
                            }}
                          >
                            ▲ Vezi mai puține
                          </button>
                        )}
                        {canShowMore && (
                          <button
                            onClick={() => setVisibleCount(v => Math.min(v + 5, total))}
                            style={{
                              padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                              border: "2px solid #1a6de0", background: "#eff6ff", color: "#1a6de0",
                              cursor: "pointer", transition: "all 0.15s",
                            }}
                          >
                            ▼ Vezi mai multe oferte ({total - visibleCount} rămase)
                          </button>
                        )}
                      </div>
                    )}
                    {!canShowMore && total > 5 && (
                      <div style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
                        Toate cele {total} oferte sunt afișate
                      </div>
                    )}
                  </>
                );
              })()
            )}

            </div>{/* end scrollable content */}

            {results.banks.length > 0 && (
              <div style={{ padding: "0 20px 20px" }}>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <button onClick={handleDownloadPDF} style={{
                    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                    color: "#fff", border: "none", borderRadius: 10,
                    padding: "13px 28px", fontSize: 14, fontWeight: 700,
                    cursor: "pointer", boxShadow: "0 4px 14px #7c3aed30",
                    display: "inline-flex", alignItems: "center", gap: 8,
                  }}>
                    <span style={{ fontSize: 17 }}>📄</span> Descarcă Oferta PDF
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={emailStatus === "sending"}
                    style={{
                      background: emailStatus === "ok"
                        ? "linear-gradient(135deg, #16a34a, #15803d)"
                        : emailStatus === "err"
                        ? "linear-gradient(135deg, #dc2626, #b91c1c)"
                        : emailStatus === "sending"
                        ? "#94a3b8"
                        : "linear-gradient(135deg, #0ea5e9, #0284c7)",
                      color: "#fff", border: "none", borderRadius: 10,
                      padding: "13px 28px", fontSize: 14, fontWeight: 700,
                      cursor: emailStatus === "sending" ? "not-allowed" : "pointer",
                      boxShadow: "0 4px 14px #0ea5e930",
                      display: "inline-flex", alignItems: "center", gap: 8,
                      transition: "background 0.3s",
                    }}>
                    <span style={{ fontSize: 17 }}>
                      {emailStatus === "sending" ? "⏳" : emailStatus === "ok" ? "✅" : emailStatus === "err" ? "❌" : "📧"}
                    </span>
                    {emailStatus === "sending" ? "Se trimite..." : emailStatus === "ok" ? "Email trimis!" : emailStatus === "err" ? "Eroare trimitere" : "Trimite pe Email"}
                  </button>
                </div>

                {emailStatus === "no_email" && (
                  <div style={{ fontSize: 11, color: "#f97316", marginTop: 6, textAlign: "center", background: "#fff7ed", borderRadius: 7, padding: "6px 12px" }}>
                    ⚠️ Completați adresa de email a solicitantului principal pentru a trimite oferta
                  </div>
                )}
                {!emailStatus && !p1.email && (
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6, textAlign: "center" }}>
                    💡 Completați email-ul solicitantului pentru a activa trimiterea
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 11, color: "#cbd5e1" }}>
          creditultau.ro — Calculator orientativ. Valorile sunt estimative.<br/><span style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.6, display: "block", marginTop: 6 }}>Acest document are scop exclusiv informativ și nu constituie o ofertă contractuală sau un angajament de finanțare. Parametrii prezentați pot fi modificați în urma procesului de analiză a eligibilității, verificării documentelor și aprobării finale din partea instituției financiare. Parametrii ofertei sunt stabiliți în baza condițiilor existente la data întocmirii prezentei simulări și pot fi modificați ulterior ca urmare a schimbărilor intervenite în politica de creditare, condițiile de piață sau criteriile interne ale finanțatorului.</span>
        </div>
      </div>
    </div>
  );
}
